/** Só assinatura: ver a nota de `art/canvas.ts` sobre por que o Phaser entra como tipo. */
import type Phaser from 'phaser';
import { Direction } from '@game/protocol';

import {
  addGridTexture,
  celLight,
  celShadow,
  context2d,
  createCanvas,
  ellipse,
  outline,
  px,
} from './canvas.ts';
import {
  CREATURE_PALETTES,
  EYE_LIGHT,
  OUTLINE,
  SKIN,
  SKIN_SHADE,
  type CreaturePalette,
} from './palette.ts';

/**
 * Atlas de criatura e de NPC.
 *
 * Mesma grade do personagem, com menos colunas: nada aqui conjura nem troca de
 * equipamento, então parado e caminhada bastam. O ataque reaproveita o último quadro da
 * caminhada com um avanço — um bote — em vez de ganhar uma animação própria: o que
 * precisa ficar legível no ataque de monstro é o número de dano, não a coreografia.
 *
 * ## Uma silhueta por espécie
 *
 * Cada espécie tem função própria e silhueta própria. Rato é rasteiro, comprido e tem
 * cauda; lobo é alto nas patas, com peito fundo e focinho longo; troll é bípede, curvado e
 * mais largo que o tile. A diferença aparece na **forma**, que é o que se enxerga de
 * relance — a cor entra depois, como confirmação.
 *
 * ## E um tamanho por espécie
 *
 * Forma e cor não bastavam: todo bicho do catálogo saía do tamanho de um tile, porque todo
 * painter desenha no mesmo quadro de 32 e com as mesmas larguras. Um Dragão do tamanho de um
 * Lobo é uma promessa desmentida antes de a luta começar — o nome diz uma coisa e a tela diz
 * outra. `Outfit.scale` é o terceiro eixo de identidade: a escala com que o quadro do painter
 * é rasterizado, do Rato em 0,75 ao Dragão em 2,5. Ver a documentação do campo.
 *
 * ## Como o corpo é construído
 *
 * Três regras, e elas explicam quase todo pixel deste arquivo.
 *
 * **Um contorno, não vários.** Cada quadro é desenhado num canvas próprio, sem contorno
 * nenhum, e só no fim `outline()` percorre a silhueta pronta. A versão anterior contornava
 * cada elipse na hora de desenhá-la, e o resultado tinha anéis escuros no meio do corpo:
 * o olho lia balões costurados, não um bicho. Contorno é propriedade da **silhueta**, e a
 * silhueta só existe depois que a última peça entrou.
 *
 * **Cinco tons, aplicados na ordem em que a luz os produz** (`volume()`): base, barriga,
 * dorso, borda. A luz vem de cima e da esquerda em toda a arte do jogo, e é a transição
 * entre os tons — não a quantidade de detalhe — que faz o desenho ter matéria. Com três
 * tons o mesmo traço vira recorte colorido, que era exatamente a queixa de que "os
 * gráficos estão ruins".
 *
 * **Ar em volta.** Nenhum bicho encosta na borda do quadro. Silhueta que preenche o
 * retângulo inteiro perde a forma: sobra massa e falta contorno para o olho seguir.
 *
 * A textura (`speckle`) entra por último e é calculada por posição, nunca sorteada: um
 * salpico aleatório mudaria a cada quadro e o pelo ficaria fervendo no lugar.
 *
 * O perfil oeste é o leste espelhado (`scale(-1, 1)`), e não um segundo desenho: um bicho
 * simétrico visto de lado é a mesma imagem virada, e manter as duas à mão só criaria a
 * chance de uma divergir da outra.
 */

export const CREATURE_FRAME = 32;
/**
 * Colunas da folha: repouso, caminhada, ataque, **morte** e **conjuração** (PRD §15.5).
 *
 * As duas últimas colunas entraram junto com as sete famílias, e não depois, pela razão que
 * §15.5 dá: a folha é o contrato entre a arte e o código, e acrescentar uma coluna depois de
 * vinte e cinco espécies desenhadas obriga a revisitar as vinte e cinco. Uma criatura que
 * conjura sem quadro de conjuração conjura de braços cruzados, e uma que morre sem quadro de
 * morte pisca e some — que é a diferença entre um abate e um bug de rede, para quem olha.
 */
export const CREATURE_COLUMNS = 14;
export const CREATURE_ROWS = 4;

/**
 * Quadro alto, para os NPCs humanos e para o que é maior que gente.
 *
 * Mercador e banqueira são gente, e gente tem a altura do personagem jogável (`characters.ts`
 * usa 32×48). Desenhados no quadro de 32 das criaturas, eles ficavam dois terços de um
 * jogador — o vendedor da praça parecia criança ao lado de quem chega para comprar. O mesmo
 * argumento vale ao contrário para Ogro, Ciclope, Lich e os elementais: um gigante que
 * coubesse em 32 px seria um gigante da altura de um lobo, e "gigante" é exatamente a
 * informação que a silhueta deve dar de graça.
 */
const TALL_FRAME = 48;

const IDLE = [0, 1];
const WALK = [2, 3, 4, 5];
const ATTACK = [6];
/** Quatro quadros: o corpo tomba e afunda. Não repete — o último fica. */
const DEATH = [7, 8, 9, 10];
/** Três quadros: recolhe, ergue, solta. */
const CAST = [11, 12, 13];

export function creatureTextureKey(outfitId: number): string {
  return `creature-${outfitId}`;
}

export type CreatureAnimation = 'idle' | 'walk' | 'attack' | 'death' | 'cast';

export function creatureAnimationKey(
  outfitId: number,
  animation: CreatureAnimation,
  direction: Direction,
): string {
  return `${creatureTextureKey(outfitId)}-${animation}-${direction}`;
}

/** Estado do corpo num quadro. Os três valores que a animação inteira usa. */
interface Pose {
  /** −1 a 1: patas alternando na caminhada. */
  step: number;
  /** Deslocamento vertical do tronco no repouso. Um pixel basta. */
  breathe: number;
  /**
   * Progresso da morte, de 0 a 1 (PRD §15.5).
   *
   * O corpo tomba para o lado e afunda no chão. Um único número, e não uma pose por quadro,
   * porque o tombo é contínuo: o painter da família o consome com uma rotação e um
   * deslocamento, e nenhuma família precisa de desenho novo para morrer.
   */
  dying: number;
  /**
   * Progresso da conjuração, de 0 a 1.
   *
   * Recolhe, ergue, solta. Como a morte, é um escalar que o painter traduz — o que faz a
   * coluna de conjuração custar uma linha por família em vez de três desenhos.
   */
  casting: number;
  /**
   * Avanço do bote, em pixels.
   *
   * Dois, e não mais: o focinho do lobo já termina a um pixel da borda do quadro, e um
   * bote maior o cortaria fora em vez de esticá-lo.
   */
  lunge: number;
}

function poseFor(column: number): Pose {
  const rest = { step: 0, breathe: 0, lunge: 0, dying: 0, casting: 0 };

  if (column === 0) return rest;
  if (column === 1) return { ...rest, breathe: -1 };
  if (column >= 2 && column <= 5) {
    return { ...rest, step: [1, 0, -1, 0][column - 2] ?? 0 };
  }
  if (column === 6) return { ...rest, breathe: -1, lunge: 2 };

  // Morte: quatro quadros, o tombo indo de um quarto a inteiro. Começa em 0,25 e não em 0
  // porque um primeiro quadro idêntico ao repouso lê como travamento.
  if (column >= 7 && column <= 10) return { ...rest, dying: (column - 6) / 4 };

  // Conjuração: recolhe (0,35), ergue (1) e solta (0,6). O pico no meio é o que faz o
  // gesto ter direção — subindo e descendo, e não crescendo até parar.
  return { ...rest, casting: [0.35, 1, 0.6][column - 11] ?? 0 };
}

type Painter = (
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  palette: CreaturePalette,
  pose: Pose,
  /** Linha do chão dentro do quadro: onde a pata encosta. */
  ground: number,
) => void;

interface Outfit {
  readonly paint: Painter;
  readonly frameHeight: number;
  /**
   * Tamanho da espécie no mundo, com o humano — mercador, banqueira, jogador — em 1.
   *
   * ## Por que um multiplicador, e não só uma altura maior
   *
   * `CreaturePalette.height` já dizia a altura do corpo, e mesmo assim rato, lobo, urso e
   * dragão saíam do mesmo tamanho na tela. O motivo é que os painters desenham **larguras
   * fixas**: o corpo do rato é uma elipse de raio 5,6 esteja `height` em 15 ou em 17, e o
   * que a altura mexia era só o comprimento das pernas. Um "Rato gigante" era o mesmo rato
   * de perna comprida, e o Dragão — 26 px num quadro de 32 — era menor que o Ogro.
   *
   * Escalar o quadro inteiro conserta as duas dimensões de uma vez e não custa desenho
   * nenhum: o painter continua desenhando no seu espaço de 32, e é a rasterização que
   * decide quantos pixels aquilo ocupa. O contorno sai depois da escala, então ele continua
   * tendo **um** pixel em qualquer tamanho — que é o que impede o bicho grande de parecer
   * uma ampliação borrada do bicho pequeno.
   *
   * ## O número sai do degrau, e não do gosto
   *
   * Cada espécie ganhou a sua escala isolada, uma de cada vez, e o resultado foi uma coluna
   * de números sem ordem entre si: o Lagarto do degrau 4 saía com 13 px de altura — menor
   * que o Rato do degrau 1 —, o Espectro do 7 tinha o tamanho do Lobo negro do 4, e o
   * Elemental de energia do degrau 8 era mais baixo que o Ciclope do 6. Tamanho é a única
   * informação que a tela dá **antes** do primeiro golpe, e ela estava mentindo: o jogador
   * decide se entra na sala olhando para o bicho, não para a tabela de §15.5.
   *
   * Agora a escala é lida do degrau da criatura (`NpcTemplate.tier`), pela régua abaixo, e o
   * ajuste dentro da faixa é só o que a anatomia pede — um Ogro é mais alto que uma Serpente
   * do mesmo degrau porque ele é bípede, não porque é mais forte:
   *
   * | degrau | escala      | altura na tela |
   * |--------|-------------|----------------|
   * | 1      | 0,75–0,875  |     12 a 13 px |
   * | 2      | 1–1,125     |     16 a 26 px |
   * | 3      | 1,25–1,375  |     21 a 40 px |
   * | 4      | 1,375–1,5   |     22 a 44 px |
   * | 5      | 1,625–1,875 |     28 a 63 px |
   * | 6      | 1,75–1,875  |     56 a 75 px |
   * | 7      | 1,875       |     60 a 71 px |
   * | 8      | 2–2,5       |     64 a 76 px |
   *
   * O piso de cada degrau é maior que o do anterior, que é a única propriedade que precisa
   * valer: dentro da faixa a anatomia manda, entre faixas manda o degrau. A régua de leitura
   * é o jogador, que tem 40 px e não muda — o degrau 4 é onde o bestiário passa a olhá-lo de
   * cima, e é também onde a escada de §15.5 deixa de ser tutorial.
   *
   * ## Sempre múltiplo de 1/8
   *
   * Um oitavo de 32 é 4 e um oitavo de 48 é 6: qualquer escala nessa grade dá quadro em
   * pixel inteiro nos dois formatos, e um quadro fracionário desalinharia a grade que o
   * Phaser recorta — meio pixel de sobra na coluna 13 aparece como a fatia do vizinho.
   */
  readonly scale: number;
}

/**
 * Um desenho por `outfitId`, na mesma ordem de `CREATURE_PALETTES` e da tabela de
 * `NpcTemplate.outfitId` em `entities/npc.ts` — os três concordam pelo índice.
 *
 * ## Uma entrada por espécie, e não por família
 *
 * A primeira versão dava um esqueleto genérico a cada família de §15.5, e o bestiário
 * inteiro dividia cinco desenhos e cinco paletas: o Javali e o Urso saíam idênticos ao
 * Lobo até na cor, o Ciclope era o Troll, o Dragão era um réptil qualquer sem asa. A
 * queixa de "a imagem não é fiel ao que a criatura é" morava toda aí — o nome prometia
 * uma anatomia que o atlas não entregava.
 *
 * Agora cada espécie tem o seu índice. As **variantes** de uma silhueta — Rato de
 * caverna, Rato gigante, Lobo negro, Troll batedor — reusam o painter da espécie-mãe e
 * divergem na paleta e na altura, que é a economia que a versão por família prometia e
 * esta mantém. Já as espécies cuja identidade é uma **anatomia** — a presa do Javali, o
 * olho único do Ciclope, a pinça do Escorpião, a asa da Vespa e do Dragão, o S sem patas
 * da Serpente — ganham painter próprio, porque nenhuma paleta transforma quatro patas em
 * nenhuma.
 */
const OUTFITS: readonly Outfit[] = [
  // 0–4: as três espécies fundadoras e os dois humanos de conversa.
  { paint: drawRat, frameHeight: CREATURE_FRAME, scale: 0.75 }, // 0 Rato — degrau 1
  { paint: drawWolf, frameHeight: CREATURE_FRAME, scale: 1.125 }, // 1 Lobo — degrau 2
  { paint: drawTroll, frameHeight: CREATURE_FRAME, scale: 1.375 }, // 2 Troll — degrau 3
  { paint: drawMerchant, frameHeight: TALL_FRAME, scale: 1 },
  { paint: drawBanker, frameHeight: TALL_FRAME, scale: 1 },
  // 5–9: as espécies-base das cinco famílias de §15.5.
  { paint: drawBat, frameHeight: CREATURE_FRAME, scale: 0.875 }, // 5 Morcego — degrau 1
  { paint: spiderPainter(false), frameHeight: CREATURE_FRAME, scale: 1 }, // 6 Aranha — degrau 2
  { paint: drawSkeleton, frameHeight: TALL_FRAME, scale: 1.375 }, // 7 Esqueleto — degrau 4
  { paint: drawEarthElemental, frameHeight: TALL_FRAME, scale: 1.625 }, // 8 El. de terra — degrau 5
  { paint: drawLizard, frameHeight: CREATURE_FRAME, scale: 1.5 }, // 9 Lagarto — degrau 4
  // 10–29: o resto do bestiário, na ordem da tabela de `npc.ts`.
  { paint: drawRat, frameHeight: CREATURE_FRAME, scale: 0.75 }, // 10 Rato de caverna — degrau 1
  { paint: drawRat, frameHeight: CREATURE_FRAME, scale: 1.125 }, // 11 Rato gigante — degrau 2
  { paint: drawBoar, frameHeight: CREATURE_FRAME, scale: 1.125 }, // 12 Javali — degrau 2
  { paint: drawBear, frameHeight: CREATURE_FRAME, scale: 1.375 }, // 13 Urso — degrau 3
  { paint: drawWolf, frameHeight: CREATURE_FRAME, scale: 1.5 }, // 14 Lobo negro — degrau 4
  { paint: drawTroll, frameHeight: CREATURE_FRAME, scale: 1.25 }, // 15 Troll batedor — degrau 3
  { paint: drawOgre, frameHeight: TALL_FRAME, scale: 1.75 }, // 16 Ogro — degrau 5
  { paint: drawCyclops, frameHeight: TALL_FRAME, scale: 1.875 }, // 17 Ciclope — degrau 6
  { paint: drawScorpion, frameHeight: CREATURE_FRAME, scale: 1.375 }, // 18 Escorpião — degrau 3
  { paint: spiderPainter(true), frameHeight: CREATURE_FRAME, scale: 1.875 }, // 19 Aranha gigante — degrau 5
  { paint: drawWasp, frameHeight: CREATURE_FRAME, scale: 1.5 }, // 20 Vespa gigante — degrau 4
  { paint: drawGhoul, frameHeight: TALL_FRAME, scale: 1.625 }, // 21 Carniçal — degrau 5
  { paint: drawSpectre, frameHeight: TALL_FRAME, scale: 1.875 }, // 22 Espectro — degrau 7
  { paint: drawLich, frameHeight: TALL_FRAME, scale: 2 }, // 23 Lich — degrau 8
  { paint: drawFireElemental, frameHeight: TALL_FRAME, scale: 1.75 }, // 24 El. de fogo — degrau 6
  { paint: drawIceElemental, frameHeight: TALL_FRAME, scale: 1.875 }, // 25 El. de gelo — degrau 7
  { paint: drawEnergyElemental, frameHeight: TALL_FRAME, scale: 2 }, // 26 El. de energia — degrau 8
  { paint: drawSerpent, frameHeight: CREATURE_FRAME, scale: 1.75 }, // 27 Serpente — degrau 5
  { paint: drawDrakonid, frameHeight: TALL_FRAME, scale: 1.875 }, // 28 Draconato — degrau 7
  // O Dragão é o único que passa de 2: ele é o teto do conteúdo e o painter o desenha
  // deitado — com 26 px de altura de paleta, a escala do Lich o deixaria mais baixo que o
  // Lich. O que ele tem de maior é o comprimento, e é em largura que os 2,5 aparecem.
  { paint: drawDragon, frameHeight: CREATURE_FRAME, scale: 2.5 }, // 29 Dragão — degrau 8

  // 30–40: os ofícios da cidade (PRD §15.8). Dez pessoas no quadro alto e na escala 1, que
  // é a de gente — um lojista de escala 1,25 seria um lojista mais alto que o jogador, e a
  // única coisa que a diferença de tamanho entre duas pessoas comunica é idade ou espécie.
  { paint: drawArmorer, frameHeight: TALL_FRAME, scale: 1 }, // 30 Armeiro
  { paint: drawApothecary, frameHeight: TALL_FRAME, scale: 1 }, // 31 Boticária
  { paint: drawTavernkeeper, frameHeight: TALL_FRAME, scale: 1 }, // 32 Taverneiro
  { paint: drawGranger, frameHeight: TALL_FRAME, scale: 1 }, // 33 Granjeiro
  { paint: drawFerryman, frameHeight: TALL_FRAME, scale: 1 }, // 34 Barqueiro
  { paint: drawTanner, frameHeight: TALL_FRAME, scale: 1 }, // 35 Curtidor
  { paint: drawJeweler, frameHeight: TALL_FRAME, scale: 1 }, // 36 Joalheira
  { paint: drawPriest, frameHeight: TALL_FRAME, scale: 1 }, // 37 Sacerdote
  { paint: drawGuard, frameHeight: TALL_FRAME, scale: 1 }, // 38 Guarda
  { paint: drawTownsfolk, frameHeight: TALL_FRAME, scale: 1 }, // 39 Aldeão
  // A galinha é o menor bicho do jogo, e é ela que dá a régua de baixo. Escala 1 no quadro de
  // 32 — e não 0,75, que foi a primeira tentativa: com o corpo ocupando 12 dos 32 px do quadro,
  // três quartos disso davam um bicho de nove pixels, em que a crista some e o que resta é uma
  // mancha creme. Em escala 1 ela tem pouco mais de um terço da altura de uma pessoa, que é a
  // proporção certa, e a crista ainda cabe.
  { paint: drawChicken, frameHeight: CREATURE_FRAME, scale: 1 }, // 40 Galinha
];

/**
 * Altura do desenho na tela, em pixels — do chão ao topo da cabeça.
 *
 * Só quem posiciona o nome precisa disto, e precisa de verdade: o rótulo era colocado a uma
 * distância fixa do tile, herdada do quadro de 48 do personagem, e com o Dragão medindo o
 * dobro disso o nome dele caía no meio do peito. A conta soma dois pixels à altura do corpo
 * porque orelha, chifre e crista são desenhados um pouco acima da linha que `height` marca.
 */
export function creatureArtHeight(outfitId: number): number {
  const outfit = OUTFITS[outfitId] ?? OUTFITS[0]!;
  const palette = CREATURE_PALETTES[outfitId] ?? CREATURE_PALETTES[0]!;
  return Math.round(Math.min(palette.height + 2, outfit.frameHeight) * outfit.scale);
}

/**
 * A folha de sprite de uma criatura, sem passar pelo Phaser.
 *
 * Separado do registro da textura porque desenhar e publicar são coisas diferentes: assim
 * a arte pode ser gerada e **olhada** fora do jogo — que é como se revisa pixel art sem
 * ter que entrar numa partida para ver o resultado de cada ajuste.
 */
export function creatureSheet(outfitId: number): HTMLCanvasElement {
  const outfit = OUTFITS[outfitId] ?? OUTFITS[0]!;
  const palette = CREATURE_PALETTES[outfitId] ?? CREATURE_PALETTES[0]!;
  const scale = outfit.scale;
  // O painter desenha sempre no espaço dele — 32 de largura, `frameHeight` de altura —, e é
  // a escala da espécie que decide quantos pixels de tela isso vira. O quadro cai em pixel
  // inteiro por construção: `Outfit.scale` é múltiplo de 1/8.
  const frameWidth = CREATURE_FRAME * scale;
  const frameHeight = outfit.frameHeight * scale;
  const ground = outfit.frameHeight - 3;

  const canvas = createCanvas(frameWidth * CREATURE_COLUMNS, frameHeight * CREATURE_ROWS);
  const ctx = context2d(canvas);

  // Quadro de trabalho: o corpo é desenhado aqui sozinho para que o contorno saia da
  // silhueta dele, e não da sombra que ele projeta no chão.
  const cell = createCanvas(frameWidth, frameHeight);
  const cellCtx = context2d(cell, true);

  for (const direction of [
    Direction.North,
    Direction.East,
    Direction.South,
    Direction.West,
  ]) {
    for (let column = 0; column < CREATURE_COLUMNS; column++) {
      cellCtx.clearRect(0, 0, frameWidth, frameHeight);
      cellCtx.save();

      // Oeste é o leste espelhado no eixo do quadro. O espelho vem **antes** da escala, no
      // espaço do quadro já escalado: espelhar depois giraria em torno do meio do espaço do
      // painter, e leste e oeste sairiam deslocados um do outro.
      if (direction === Direction.West) {
        cellCtx.translate(frameWidth, 0);
        cellCtx.scale(-1, 1);
      }

      cellCtx.scale(scale, scale);

      const facing = direction === Direction.West ? Direction.East : direction;
      const pose = poseFor(column);

      // O tombo de morte mora aqui, e não em cada painter: é uma transformação do quadro
      // inteiro. A versão que vivia dentro do painter de família deixava rato, lobo e
      // troll — que nunca a tiveram — morrendo em quatro quadros idênticos, o que lê como
      // travamento, não como abate. O pivô é o pé: girar pelo centro faria o corpo
      // levantar voo em vez de cair.
      if (pose.dying > 0) {
        cellCtx.translate(CREATURE_FRAME / 2, ground);
        cellCtx.rotate((pose.dying * Math.PI) / 2.4);
        cellCtx.translate(-CREATURE_FRAME / 2, -ground + pose.dying * 3);
        cellCtx.globalAlpha = 1 - pose.dying * 0.35;
      }

      outfit.paint(cellCtx, facing, palette, pose, ground);

      // Conjuração: um brilho na frente do corpo, na cor de detalhe da espécie — é o que
      // distingue o quadro de conjurar do de atacar sem exigir braços novos em trinta
      // painters. Quem quiser um gesto próprio ainda pode ler `pose.casting`.
      if (pose.casting > 0) {
        const top = ground - Math.min(palette.height, outfit.frameHeight - 6);
        ellipse(
          cellCtx,
          CREATURE_FRAME / 2 + 7,
          top + 6,
          1.5 + pose.casting * 2,
          1.5 + pose.casting * 2,
          palette.detail,
        );
      }

      cellCtx.restore();
      outline(cellCtx, OUTLINE);

      ctx.save();
      ctx.translate(column * frameWidth, direction * frameHeight);
      // A sombra é do tamanho do bicho, então ela também é desenhada no espaço do painter.
      ctx.scale(scale, scale);
      shadow(ctx, palette, ground);
      ctx.setTransform(1, 0, 0, 1, column * frameWidth, direction * frameHeight);
      ctx.drawImage(cell, 0, 0);
      ctx.restore();
    }
  }

  return canvas;
}

export function buildCreatureAtlases(scene: Phaser.Scene): void {
  OUTFITS.forEach((outfit, outfitId) => {
    const key = creatureTextureKey(outfitId);
    if (scene.textures.exists(key)) return;

    addGridTexture(
      scene,
      key,
      creatureSheet(outfitId),
      CREATURE_FRAME * outfit.scale,
      outfit.frameHeight * outfit.scale,
      CREATURE_COLUMNS,
      CREATURE_ROWS,
    );
    registerAnimations(scene, outfitId);
  });
}

// --- Ferramentas de desenho --------------------------------------------------

/** Sombra no chão. Não acompanha o `breathe` — sombra que sobe denuncia o truque. */
function shadow(ctx: CanvasRenderingContext2D, palette: CreaturePalette, ground: number): void {
  // Bicho rasteiro derrama sombra larga; gente em pé, quase nenhuma. O teto existe porque
  // a altura de um humanoide diz o comprimento dele, não a área que ele cobre no chão.
  const width = Math.min(Math.round(palette.height * 0.42), 9);
  ctx.globalAlpha = 0.3;
  ellipse(ctx, CREATURE_FRAME / 2, ground + 1, width, width * 0.35, '#000000');
  ctx.globalAlpha = 1;
}

/**
 * Massa com volume, sem contorno: base, barriga, dorso, borda de luz.
 *
 * `lean` inclina o dorso iluminado. De perfil ele acompanha o lombo, para o lado de onde a
 * luz vem; de frente fica quase no centro, porque aí o que se vê é o peito.
 */
function volume(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  p: CreaturePalette,
  lean = -0.2,
): void {
  ellipse(ctx, cx, cy, rx, ry, p.body);
  ellipse(ctx, cx, cy + ry * 0.55, rx * 0.9, ry * 0.45, p.shade);
  ellipse(ctx, cx + rx * lean, cy - ry * 0.3, rx * 0.7, ry * 0.55, p.light);
  ellipse(ctx, cx + rx * (lean - 0.05), cy - ry * 0.62, rx * 0.36, ry * 0.2, p.highlight);
}

/**
 * Ruído estável: a mesma posição devolve sempre o mesmo valor.
 *
 * Estabilidade é o requisito, não a qualidade da distribuição — textura sorteada a cada
 * quadro faz o pelo ferver, que é pior do que não ter textura nenhuma.
 */
function noise(x: number, y: number, seed: number): number {
  const n = Math.imul((x | 0) + seed * 977, 73856093) ^ Math.imul((y | 0) + seed * 31, 19349663);
  return ((n >>> 0) % 997) / 997;
}

/** Salpico dentro de um retângulo: pelo curto, verruga, fiapo de lã. */
function speckle(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  color: string,
  density: number,
  seed = 0,
): void {
  for (let iy = 0; iy < h; iy++) {
    for (let ix = 0; ix < w; ix++) {
      if (noise(x + ix, y + iy, seed) < density) px(ctx, x + ix, y + iy, 1, 1, color);
    }
  }
}

/** Traço curvo de espessura fixa: cauda, rabo, alça. */
function curve(
  ctx: CanvasRenderingContext2D,
  from: readonly [number, number],
  control: readonly [number, number],
  to: readonly [number, number],
  width: number,
  color: string,
): void {
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(from[0], from[1]);
  ctx.quadraticCurveTo(control[0], control[1], to[0], to[1]);
  ctx.stroke();
}

/**
 * Perna com articulação: coxa larga, canela fina, pata escura no chão.
 *
 * Três peças em vez de um retângulo. O retângulo é o que fazia as criaturas parecerem
 * mesa — quatro paus iguais sob uma massa —, e a diferença de largura entre coxa e canela
 * custa dois pixels e resolve a leitura. A perna do outro lado do corpo entra com `back`,
 * um tom abaixo: é o que dá profundidade a quatro patas em oito pixels de largura.
 */
function leg(
  ctx: CanvasRenderingContext2D,
  x: number,
  top: number,
  bottom: number,
  thigh: number,
  p: CreaturePalette,
  back = false,
): void {
  const body = back ? p.shade : p.body;
  const dark = back ? p.deep : p.shade;
  const knee = top + Math.round((bottom - top) * 0.5);
  // A canela é um pixel mais fina que a coxa, nunca menos que um: numa pata de dois
  // pixels a conta antiga (`thigh - 2`) dava zero, e o bicho ficava com tocos.
  const shin = Math.max(1, thigh - 1);

  px(ctx, x, top, thigh, knee - top, body);
  px(ctx, x + thigh - 1, top, 1, knee - top, dark);
  px(ctx, x + Math.floor((thigh - shin) / 2), knee, shin, bottom - knee - 1, body);
  px(ctx, x, bottom - 1, thigh, 1, dark);
}

/**
 * Braço de bruto: braço, antebraço deslocado para fora e punho fechado.
 *
 * O deslocamento de um pixel no cotovelo é o que transforma a laje em membro. Sem ele o
 * braço é um retângulo do ombro ao chão, e um retângulo colado no tronco não lê como
 * braço — lê como a lateral do tronco, e o troll vira um monte verde com cara.
 */
function arm(
  ctx: CanvasRenderingContext2D,
  x: number,
  top: number,
  p: CreaturePalette,
  back: boolean,
): void {
  const body = back ? p.shade : p.body;

  px(ctx, x, top, 4, 7, body);
  if (!back) px(ctx, x, top, 2, 7, p.light);
  px(ctx, x - (back ? -1 : 1), top + 6, 4, 6, body);
  ellipse(ctx, x + (back ? 3 : 1), top + 12, 2.8, 2.6, body);
}

/** Olho da espécie: dois pixels de cor e um de luz. */
function eye(ctx: CanvasRenderingContext2D, x: number, y: number, color: string): void {
  px(ctx, x, y, 2, 2, color);
  px(ctx, x, y, 1, 1, EYE_LIGHT);
}

/**
 * Olho de conta: escuro, com um brilho da cor da espécie.
 *
 * É o olho de bicho pequeno. Um par de olhos coloridos de 2×2 numa cabeça de nove pixels
 * come metade da cara — o rato ficava com cara de máscara, e não de rato.
 */
function beadEye(ctx: CanvasRenderingContext2D, x: number, y: number, color: string): void {
  px(ctx, x, y, 2, 2, OUTLINE);
  px(ctx, x, y, 1, 1, color);
}

// --- Rato --------------------------------------------------------------------

/**
 * Rasteiro e comprido, com cauda fina e orelhas grandes demais para a cabeça.
 *
 * As orelhas são o que identifica o rato de longe: elas quebram a silhueta oval em dois
 * picos, e nenhuma outra criatura do catálogo tem isso. O corpo é uma gota — cheio na
 * garupa, estreitando até o focinho —, e não um oval: rato tem traseiro pesado e cabeça
 * baixa, e é daí que vem a impressão de bicho agachado.
 */
function drawRat(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  const cx = CREATURE_FRAME / 2;
  const t = ground - p.height + pose.breathe;
  const swing = Math.round(pose.step * 2);

  if (direction === Direction.East) {
    const f = pose.lunge;

    // Cauda: desce da garupa e volta a subir, fina e sem alcançar a borda.
    curve(ctx, [cx - 6, t + 9], [cx - 12, t + 12], [cx - 11, t + 4], 2, p.shade);
    curve(ctx, [cx - 6, t + 9], [cx - 11, t + 11], [cx - 10, t + 5], 1, p.detail);

    leg(ctx, cx - 5, t + 10, ground, 2, p, true);
    leg(ctx, cx + 4 + f, t + 10, ground, 2, p, true);
    leg(ctx, cx - 2, t + 10 + swing, ground, 2, p);
    leg(ctx, cx + 7 + f, t + 10 - swing, ground, 2, p);

    // Garupa, ombro e cabeça: três volumes decrescentes, e o último **mais baixo** que o
    // anterior. É esse degrau que o contorno transforma em pescoço; alinhados, os três
    // viram um tubo, que foi o rato deitado da primeira tentativa.
    volume(ctx, cx - 3 + f, t + 6.5, 5.6, 4.2, p, -0.25);
    volume(ctx, cx + 2 + f, t + 7.5, 4, 3.4, p, -0.1);
    speckle(ctx, cx - 8 + f, t + 3, 11, 4, p.highlight, 0.07, 1);

    volume(ctx, cx + 6 + f, t + 9, 3, 2.8, p, -0.1);
    px(ctx, cx + 8 + f, t + 8, 4, 3, p.body);
    px(ctx, cx + 8 + f, t + 10, 4, 1, p.shade);
    px(ctx, cx + 11 + f, t + 9, 2, 2, p.detail);
    px(ctx, cx + 12 + f, t + 9, 1, 1, p.accent);

    // Orelha: disco com miolo rosado, no alto da nuca.
    ellipse(ctx, cx + 4 + f, t + 4.5, 2.3, 2.5, p.shade);
    ellipse(ctx, cx + 4.3 + f, t + 4.8, 1.2, 1.4, p.accent);

    beadEye(ctx, cx + 7 + f, t + 8, p.eye);
    return;
  }

  if (direction === Direction.North) {
    // De costas: lombo, nuca e a cauda saindo por baixo, para o lado.
    curve(ctx, [cx + 1, ground - 4], [cx + 9, ground - 1], [cx + 10, t + 7], 2, p.shade);
    curve(ctx, [cx + 1, ground - 4], [cx + 8, ground - 2], [cx + 9, t + 8], 1, p.detail);

    leg(ctx, cx - 7, t + 10, ground, 3, p, true);
    leg(ctx, cx + 4, t + 10, ground, 3, p, true);

    volume(ctx, cx, t + 9, 6, 5, p, -0.15);
    volume(ctx, cx, t + 4.5, 4, 3.6, p, -0.15);
    speckle(ctx, cx - 6, t + 2, 12, 7, p.highlight, 0.06, 2);

    // Orelhas vistas por trás: o miolo não aparece, só o vulto.
    ellipse(ctx, cx - 4.5, t + 1.5, 2.6, 2.6, p.shade);
    ellipse(ctx, cx + 4.5, t + 1.5, 2.6, 2.6, p.shade);
    return;
  }

  // De frente: corpo curto e largo, cabeça encostada nos ombros.
  curve(ctx, [cx + 3, ground - 4], [cx + 10, ground - 2], [cx + 11, t + 8], 2, p.shade);
  curve(ctx, [cx + 3, ground - 4], [cx + 9, ground - 3], [cx + 10, t + 9], 1, p.detail);

  leg(ctx, cx - 7, t + 10, ground, 3, p, true);
  leg(ctx, cx + 4, t + 10, ground, 3, p, true);

  volume(ctx, cx, t + 9.5, 5.8, 4.5, p, -0.1);
  speckle(ctx, cx - 5, t + 7, 10, 4, p.highlight, 0.06, 3);

  // Patinhas dianteiras, encostadas no chão à frente do peito.
  px(ctx, cx - 4, ground - 3 + swing, 2, 3, p.detail);
  px(ctx, cx + 2, ground - 3 - swing, 2, 3, p.detail);

  volume(ctx, cx, t + 5, 4.2, 3.8, p, -0.1);

  ellipse(ctx, cx - 4.2, t + 2, 2.5, 2.7, p.body);
  ellipse(ctx, cx + 4.2, t + 2, 2.5, 2.7, p.body);
  ellipse(ctx, cx - 4.2, t + 2.3, 1.3, 1.5, p.accent);
  ellipse(ctx, cx + 4.2, t + 2.3, 1.3, 1.5, p.accent);

  // Focinho: cunha clara descendo da cara, com o nariz na ponta.
  ellipse(ctx, cx, t + 7, 2.2, 1.8, p.detail);
  px(ctx, cx - 1, t + 7, 2, 1, p.accent);

  px(ctx, cx - 8, t + 6, 4, 1, p.highlight);
  px(ctx, cx + 4, t + 6, 4, 1, p.highlight);

  beadEye(ctx, cx - 3, t + 4, p.eye);
  beadEye(ctx, cx + 1, t + 4, p.eye);
}

// --- Lobo --------------------------------------------------------------------

/**
 * Alto nas patas, com peito fundo, focinho longo e cauda baixa.
 *
 * A altura das patas é o traço que separa lobo de rato: o rato encosta a barriga no chão,
 * o lobo tem vão embaixo. É a diferença que se lê primeiro, antes de qualquer detalhe. O
 * segundo traço é a linha das costas — alta na cernelha, caindo até a garupa —, que é o
 * que faz o animal parecer pronto para avançar mesmo parado.
 */
function drawWolf(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  const cx = CREATURE_FRAME / 2;
  const t = ground - p.height + pose.breathe;
  const swing = Math.round(pose.step * 3);

  if (direction === Direction.East) {
    const f = pose.lunge;

    // Cauda baixa e cheia, com a ponta clara colada à curva.
    curve(ctx, [cx - 5, t + 10], [cx - 11, t + 10], [cx - 11, t + 4], 4, p.shade);
    curve(ctx, [cx - 5, t + 10], [cx - 10, t + 10], [cx - 10, t + 5], 2, p.body);
    ellipse(ctx, cx - 10.5, t + 4, 1.6, 1.6, p.detail);

    leg(ctx, cx - 6, t + 13, ground, 3, p, true);
    leg(ctx, cx + 3 + f, t + 13, ground, 3, p, true);
    leg(ctx, cx - 3, t + 13 + swing, ground, 3, p);
    leg(ctx, cx + 6 + f, t + 13 - swing, ground, 3, p);

    // Garupa e peito: dois volumes, o da frente mais alto e mais fundo.
    volume(ctx, cx - 3, t + 11, 6, 4.6, p, -0.25);
    volume(ctx, cx + 3 + f, t + 10.5, 5.4, 5, p, -0.12);
    // Pelo do lombo em `light`, e ralo: no tom de borda e mais denso, virava neve caída
    // nas costas do animal.
    speckle(ctx, cx - 8, t + 7, 13, 4, p.light, 0.06, 4);

    // Pescoço grosso subindo para a cabeça, com a juba quebrando a linha do ombro.
    px(ctx, cx + 5 + f, t + 4, 5, 8, p.body);
    px(ctx, cx + 5 + f, t + 4, 2, 8, p.light);
    speckle(ctx, cx + 3 + f, t + 4, 4, 9, p.detail, 0.16, 5);

    volume(ctx, cx + 9 + f, t + 4, 3.6, 3.2, p, -0.2);

    // Focinho em cunha, ponta escura.
    px(ctx, cx + 11 + f, t + 3, 5, 3, p.body);
    px(ctx, cx + 11 + f, t + 5, 5, 1, p.shade);
    px(ctx, cx + 15 + f, t + 3, 2, 2, p.accent);

    // Orelha triangular, em pé.
    px(ctx, cx + 7 + f, t, 3, 4, p.body);
    px(ctx, cx + 8 + f, t + 1, 1, 2, p.accent);

    eye(ctx, cx + 10 + f, t + 3, p.eye);
    return;
  }

  if (direction === Direction.North) {
    // De costas: garupa alta, cauda caindo para o lado, orelhas de trás.
    curve(ctx, [cx + 1, t + 9], [cx + 8, t + 13], [cx + 7, ground - 3], 4, p.shade);
    ellipse(ctx, cx + 7, ground - 3, 2, 2, p.detail);

    leg(ctx, cx - 8, t + 13, ground, 3, p, true);
    leg(ctx, cx + 5, t + 13, ground, 3, p, true);
    leg(ctx, cx - 4, t + 13 + swing, ground, 3, p);
    leg(ctx, cx + 1, t + 13 - swing, ground, 3, p);

    volume(ctx, cx, t + 11, 7, 6, p, -0.15);
    speckle(ctx, cx - 6, t + 7, 12, 5, p.light, 0.06, 6);

    volume(ctx, cx, t + 4.5, 5, 4, p, -0.15);
    px(ctx, cx - 6, t + 1, 3, 4, p.shade);
    px(ctx, cx + 3, t + 1, 3, 4, p.shade);
    return;
  }

  // De frente: peito claro entre as patas, cabeça larga, focinho baixo.
  leg(ctx, cx - 8, t + 13, ground, 3, p, true);
  leg(ctx, cx + 5, t + 13, ground, 3, p, true);
  leg(ctx, cx - 4, t + 13 + swing, ground, 3, p);
  leg(ctx, cx + 1, t + 13 - swing, ground, 3, p);

  volume(ctx, cx, t + 12, 6.5, 6, p, -0.1);
  ellipse(ctx, cx, t + 14, 2.6, 3.6, p.detail);
  speckle(ctx, cx - 5, t + 9, 10, 4, p.light, 0.06, 7);

  volume(ctx, cx, t + 4.5, 5, 4.4, p, -0.1);

  // Orelhas triangulares, afastadas: é o que dá a cara larga.
  px(ctx, cx - 7, t + 1, 3, 5, p.body);
  px(ctx, cx + 4, t + 1, 3, 5, p.body);
  px(ctx, cx - 6, t + 2, 1, 3, p.accent);
  px(ctx, cx + 5, t + 2, 1, 3, p.accent);

  // Máscara clara em volta do focinho, ponta escura.
  ellipse(ctx, cx, t + 7, 3.2, 2.4, p.detail);
  ellipse(ctx, cx, t + 7.6, 1.6, 1.2, p.accent);

  eye(ctx, cx - 4, t + 3, p.eye);
  eye(ctx, cx + 2, t + 3, p.eye);
}

// --- Troll -------------------------------------------------------------------

/**
 * Bípede curvado, com braços que passam do joelho e cabeça enfiada nos ombros.
 *
 * É a única criatura do catálogo que fica **de pé**, e é isso que a torna reconhecível
 * mesmo pequena na tela: entre dois quadrúpedes, uma silhueta vertical salta. O peso está
 * todo em cima — ombros largos, quadril estreito, pernas curtas —, e os braços descem
 * **fora** do tronco, com vão visível entre eles e o corpo. Sem esse vão o troll vira um
 * monte verde: braço colado em tronco não lê como braço.
 */
function drawTroll(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  const cx = CREATURE_FRAME / 2;
  const t = ground - p.height + pose.breathe;
  const swing = Math.round(pose.step * 2);
  const f = direction === Direction.East ? pose.lunge : 0;

  // Pernas curtas e grossas, com vão entre elas.
  leg(ctx, cx - 7, t + 19 + swing, ground, 5, p, true);
  leg(ctx, cx + 2, t + 19 - swing, ground, 5, p);

  if (direction === Direction.East) {
    // Braço de trás primeiro, para o tronco cobrir o ombro dele.
    arm(ctx, cx - 6 + f, t + 12, p, true);

    volume(ctx, cx + f, t + 16, 6, 5.5, p, -0.2);
    ellipse(ctx, cx + f, t + 11.5, 5.6, 3, p.light);
    speckle(ctx, cx - 5 + f, t + 10, 11, 9, p.accent, 0.09, 8);

    // Braço da frente, longo, quase raspando o chão.
    arm(ctx, cx + 4 + f, t + 12, p, false);

    // Cabeça enfiada, queixo pesado para a frente.
    volume(ctx, cx + 2 + f, t + 6, 4.6, 4.2, p, -0.2);
    px(ctx, cx + 5 + f, t + 6, 3, 4, p.body);
    px(ctx, cx + 5 + f, t + 8, 3, 2, p.shade);
    px(ctx, cx + 6 + f, t + 9, 2, 2, p.detail);
    px(ctx, cx + f, t + 2, 7, 2, p.deep);

    eye(ctx, cx + 3 + f, t + 4, p.eye);
    return;
  }

  if (direction === Direction.North) {
    arm(ctx, cx - 12, t + 12, p, true);
    arm(ctx, cx + 8, t + 12, p, true);

    volume(ctx, cx, t + 16, 6.8, 5.5, p, -0.15);
    ellipse(ctx, cx, t + 11.5, 6.6, 3.2, p.light);
    speckle(ctx, cx - 6, t + 9, 13, 10, p.accent, 0.1, 9);

    volume(ctx, cx, t + 6, 5, 4.4, p, -0.15);
    speckle(ctx, cx - 4, t + 3, 8, 4, p.accent, 0.12, 10);
    return;
  }

  // De frente: ombros largos, braços caídos dos dois lados, presas.
  arm(ctx, cx - 12, t + 12, p, false);
  arm(ctx, cx + 8, t + 12, p, true);

  volume(ctx, cx, t + 16, 6.8, 5.5, p, -0.15);
  ellipse(ctx, cx, t + 11.5, 6.6, 3.2, p.light);
  speckle(ctx, cx - 6, t + 9, 13, 10, p.accent, 0.09, 11);

  volume(ctx, cx, t + 6, 5, 4.4, p, -0.15);

  // Sobrancelha pesada: uma faixa escura sobre os olhos é o que dá cara de bruto.
  px(ctx, cx - 5, t + 3, 10, 2, p.deep);

  // Boca larga com duas presas subindo da mandíbula.
  px(ctx, cx - 3, t + 9, 7, 1, p.deep);
  px(ctx, cx - 3, t + 8, 2, 2, p.detail);
  px(ctx, cx + 2, t + 8, 2, 2, p.detail);

  eye(ctx, cx - 4, t + 5, p.eye);
  eye(ctx, cx + 2, t + 5, p.eye);
}

// --- NPCs de conversa --------------------------------------------------------

/**
 * O corpo comum dos NPCs humanos.
 *
 * As proporções são as do personagem jogável (`characters.ts`): cabeça grande, tronco
 * curto, pernas de doze pixels. Não é economia — é o que faz o mercador e o jogador
 * parecerem a mesma espécie parados lado a lado, que é justamente onde eles ficam.
 *
 * Mercador e banqueira compartilham este esqueleto e divergem no que se vê primeiro: o
 * mercador é largo, de túnica curta e avental, com as pernas à mostra; a banqueira é
 * estreita, de manto até o chão. A silhueta separa os dois antes de qualquer cor.
 */
function drawVillager(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
  robe: boolean,
): void {
  const cx = CREATURE_FRAME / 2;
  const t = ground - p.height + pose.breathe;
  const shoulders = t + 14;
  const profile = direction === Direction.East;

  if (robe) {
    // Manto até o chão: um trapézio, largo embaixo. Nenhuma perna aparece.
    ctx.fillStyle = p.body;
    ctx.beginPath();
    ctx.moveTo(cx - 6, shoulders);
    ctx.lineTo(cx + 6, shoulders);
    ctx.lineTo(cx + 9, ground);
    ctx.lineTo(cx - 9, ground);
    ctx.closePath();
    ctx.fill();

    // Pregas: faixas verticais escuras. É o que impede o manto de virar um cone liso.
    px(ctx, cx - 4, shoulders + 5, 1, ground - shoulders - 6, p.shade);
    px(ctx, cx + 1, shoulders + 7, 1, ground - shoulders - 8, p.shade);
    px(ctx, cx + 4, shoulders + 4, 1, ground - shoulders - 5, p.shade);
    px(ctx, cx - 8, ground - 3, 16, 3, p.shade);
    px(ctx, cx - 6, shoulders + 1, 3, ground - shoulders - 4, p.light);
  } else {
    // Calça e botas sob a túnica.
    px(ctx, cx - 5, ground - 12, 4, 12, p.deep);
    px(ctx, cx + 1, ground - 12, 4, 12, p.shade);
    px(ctx, cx - 6, ground - 2, 5, 2, p.deep);
    px(ctx, cx + 1, ground - 2, 5, 2, p.deep);

    const width = profile ? 10 : 12;
    px(ctx, cx - width / 2, shoulders, width, 15, p.body);
    px(ctx, cx - width / 2, shoulders + 9, width, 6, p.shade);
    px(ctx, cx - width / 2, shoulders, 3, 15, p.light);
  }

  // Braços caídos, mãos de pele na ponta.
  const armTop = shoulders + 2;
  px(ctx, cx - 8, armTop, 3, 9, p.shade);
  px(ctx, cx + 5, armTop, 3, 9, p.shade);
  px(ctx, cx - 8, armTop + 9, 3, 3, SKIN);
  px(ctx, cx + 5, armTop + 9, 3, 3, SKIN);

  // Cabeça.
  const headY = t + 6;
  ellipse(ctx, cx, headY, 6, 6, SKIN);
  px(ctx, cx - 3, headY + 4, 6, 2, SKIN_SHADE);

  if (direction === Direction.North) {
    ellipse(ctx, cx, headY - 1, 6, 5.5, p.accent);
    px(ctx, cx - 6, headY - 1, 12, 4, p.accent);
    return;
  }

  // Cabelo: calota mais franja, que é a leitura mais barata de um rosto de frente.
  ellipse(ctx, cx, headY - 2.5, 6, 4, p.accent);
  px(ctx, cx - 6, headY - 3, 2, 5, p.accent);
  px(ctx, cx + 4, headY - 3, 2, 5, p.accent);

  if (profile) {
    px(ctx, cx + 2, headY + 1, 2, 2, p.eye);
    px(ctx, cx + 2, headY + 1, 1, 1, EYE_LIGHT);
    return;
  }

  px(ctx, cx - 4, headY + 1, 2, 2, p.eye);
  px(ctx, cx + 2, headY + 1, 2, 2, p.eye);
  px(ctx, cx - 4, headY + 1, 1, 1, EYE_LIGHT);
  px(ctx, cx + 2, headY + 1, 1, 1, EYE_LIGHT);
  px(ctx, cx - 1, headY + 4, 2, 1, SKIN_SHADE);
}

/**
 * Mercador: avental de couro e uma bolsa no cinto.
 *
 * O avental é o uniforme de quem trabalha com as mãos, e a bolsa diz o que ele faz com
 * elas. Os dois juntos respondem "com quem eu falo para vender isto?" sem o jogador ter
 * que clicar para descobrir.
 */
function drawMerchant(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  drawVillager(ctx, direction, p, pose, ground, false);

  const cx = CREATURE_FRAME / 2;
  const t = ground - p.height + pose.breathe;
  const shoulders = t + 14;

  if (direction === Direction.North) return;

  // Avental: retângulo claro do peito ao joelho, com duas alças no pescoço.
  px(ctx, cx - 4, shoulders + 4, 8, 12, p.detail);
  px(ctx, cx - 4, shoulders + 14, 8, 2, p.shade);
  px(ctx, cx - 3, shoulders + 1, 1, 4, p.detail);
  px(ctx, cx + 2, shoulders + 1, 1, 4, p.detail);

  // Bolsa de moedas no cinto.
  ellipse(ctx, cx + 5, shoulders + 12, 2.2, 2.6, p.accent);
  px(ctx, cx + 4, shoulders + 9, 3, 1, p.deep);
}

/**
 * Banqueira: manto azul-noite, gola dourada e o livro de contas contra o peito.
 *
 * O ouro aparece só na gola e no fecho do livro — pouco e no alto, onde o olho vai
 * primeiro. Um manto inteiro dourado leria como tesouro, e ela guarda o dinheiro dos
 * outros.
 */
function drawBanker(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  drawVillager(ctx, direction, p, pose, ground, true);

  const cx = CREATURE_FRAME / 2;
  const t = ground - p.height + pose.breathe;
  const shoulders = t + 14;

  px(ctx, cx - 6, shoulders, 12, 2, p.detail);

  if (direction === Direction.North) return;

  px(ctx, cx - 1, shoulders + 2, 2, 3, p.detail);

  // Livro de contas, seguro contra o peito.
  px(ctx, cx + 2, shoulders + 7, 6, 7, p.deep);
  px(ctx, cx + 3, shoulders + 8, 4, 5, p.detail);
  px(ctx, cx + 3, shoulders + 8, 1, 5, p.shade);
}

// --- Os ofícios da cidade (PRD §15.8) ------------------------------------------
//
// Onze desenhos, e todos os dez humanos saem do mesmo `drawVillager` — o esqueleto de gente,
// com ou sem manto. O que separa um armeiro de um taverneiro são **de seis a doze pixels**
// postos no lugar certo, e é de propósito: a alternativa era um esqueleto por ofício, dez
// vezes o custo para produzir dez pessoas que têm o mesmo corpo.
//
// A regra de qual pixel gastar é a mesma do bestiário — "o traço que dá nome à espécie vem
// primeiro" — traduzida para gente: **o objeto do ofício antes da roupa do ofício**. O
// chapéu de palha do granjeiro, a lança do guarda, o remo do barqueiro e o martelo do armeiro
// alteram a **silhueta**, e silhueta é o que sobrevive a 32 px de largura na beira da tela.
// Avental e gola vêm depois, como confirmação de perto.
//
// Nenhum deles desenha nada quando `direction` é norte: de costas não se vê ferramenta, nem
// avental, nem rosto. É a mesma economia que `drawMerchant` e `drawBanker` já faziam.

/** A linha do ombro, de onde tudo o que é vestido pende. Igual em todos os dez. */
function shoulderLine(p: CreaturePalette, pose: Pose, ground: number): number {
  return ground - p.height + pose.breathe + 14;
}

/**
 * Avental de trabalho: retângulo do peito ao joelho, com duas alças no pescoço.
 *
 * Armeiro, taverneiro e curtidor usam o mesmo, em cores diferentes — é o uniforme de quem
 * trabalha com as mãos, e é a peça que o Mercador já tinha. Compartilhá-la é o que deixa a
 * diferença entre os três morar no objeto que cada um carrega.
 */
function apron(
  ctx: CanvasRenderingContext2D,
  p: CreaturePalette,
  shoulders: number,
  color: string,
): void {
  const cx = CREATURE_FRAME / 2;
  px(ctx, cx - 4, shoulders + 4, 8, 12, color);
  px(ctx, cx - 4, shoulders + 14, 8, 2, p.shade);
  px(ctx, cx - 3, shoulders + 1, 1, 4, color);
  px(ctx, cx + 2, shoulders + 1, 1, 4, color);
}

/**
 * Haste vertical na mão direita: lança, remo, forcado.
 *
 * Uma linha de dois pixels de largura que sobe acima da cabeça. É o recurso mais barato de
 * silhueta que existe neste arquivo: o contorno de um humanoide com uma vara vertical ao lado
 * é reconhecível a qualquer distância, e nenhuma cor de roupa consegue o mesmo.
 */
function stave(
  ctx: CanvasRenderingContext2D,
  x: number,
  top: number,
  bottom: number,
  color: string,
  light: string,
): void {
  px(ctx, x, top, 2, bottom - top, color);
  px(ctx, x, top, 1, bottom - top, light);
}

/** Armeiro: avental de couro queimado, faixa na testa e o martelo apoiado no ombro. */
function drawArmorer(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  drawVillager(ctx, direction, p, pose, ground, false);

  const cx = CREATURE_FRAME / 2;
  const shoulders = shoulderLine(p, pose, ground);
  const headY = ground - p.height + pose.breathe + 6;

  // A faixa que segura o cabelo longe da forja. Vale também de costas, e é a única coisa
  // que este painter desenha voltado para o norte.
  px(ctx, cx - 6, headY - 3, 12, 2, p.detail);

  if (direction === Direction.North) return;

  apron(ctx, p, shoulders, p.detail);

  // Martelo: cabo curto na mão direita e a cabeça de ferro logo acima do ombro. Ele sobe
  // acima da linha do ombro de propósito — é o que faz a silhueta ter uma saliência que
  // nenhum outro ofício tem.
  stave(ctx, cx + 6, shoulders - 4, shoulders + 9, p.accent, celLight(p.accent, 0.34));
  px(ctx, cx + 4, shoulders - 7, 7, 4, p.highlight);
  px(ctx, cx + 4, shoulders - 7, 7, 1, '#d8dee6');
  px(ctx, cx + 4, shoulders - 4, 7, 1, p.shade);
}

/** Boticária: manto verde-garrafa, gola clara e o frasco erguido contra o peito. */
function drawApothecary(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  drawVillager(ctx, direction, p, pose, ground, true);

  const cx = CREATURE_FRAME / 2;
  const shoulders = shoulderLine(p, pose, ground);

  px(ctx, cx - 6, shoulders, 12, 2, p.detail);

  if (direction === Direction.North) return;

  // Frasco: gargalo estreito, bojo largo, uma linha de luz no vidro. O bojo é claro contra
  // o manto escuro, que é o que o faz aparecer sem precisar de tamanho.
  px(ctx, cx + 3, shoulders + 5, 2, 3, p.detail);
  ellipse(ctx, cx + 4, shoulders + 10, 3, 3.4, p.detail);
  ellipse(ctx, cx + 3, shoulders + 9, 1, 1.4, '#ffffff');
  px(ctx, cx + 3, shoulders + 4, 3, 1, p.accent);

  // Cinto de bolsos, que é onde a erva vai. Duas marcas, e não seis: seis viram listra.
  px(ctx, cx - 5, shoulders + 13, 10, 2, p.deep);
  px(ctx, cx - 4, shoulders + 12, 2, 3, p.accent);
  px(ctx, cx + 1, shoulders + 12, 2, 3, p.accent);
}

/** Taverneiro: avental branco de cozinha e a caneca na mão. */
function drawTavernkeeper(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  drawVillager(ctx, direction, p, pose, ground, false);

  const cx = CREATURE_FRAME / 2;
  const shoulders = shoulderLine(p, pose, ground);

  if (direction === Direction.North) return;

  apron(ctx, p, shoulders, p.detail);
  // Pano por cima do ombro esquerdo: o detalhe que separa cozinha de forja, já que o
  // avental dos dois tem a mesma forma.
  px(ctx, cx - 8, shoulders + 1, 3, 7, p.detail);

  // Caneca: corpo de barro e a asa em C. Pequena, e mesmo assim reconhecível — é a asa que
  // faz a leitura, e ela cabe em três pixels.
  px(ctx, cx + 4, shoulders + 8, 5, 5, p.highlight);
  px(ctx, cx + 4, shoulders + 8, 5, 1, '#ffffff');
  px(ctx, cx + 9, shoulders + 9, 1, 3, p.highlight);
  px(ctx, cx + 4, shoulders + 12, 5, 1, p.deep);
}

/** Granjeiro: chapéu de palha de aba larga — a silhueta é o chapéu. */
function drawGranger(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  drawVillager(ctx, direction, p, pose, ground, false);

  const cx = CREATURE_FRAME / 2;
  const shoulders = shoulderLine(p, pose, ground);
  const headY = ground - p.height + pose.breathe + 6;

  // A aba, e ela vale nas quatro direções: um chapéu que desaparece quando o granjeiro dá
  // as costas seria um granjeiro que muda de pessoa ao virar.
  px(ctx, cx - 9, headY - 2, 18, 3, p.detail);
  px(ctx, cx - 9, headY - 2, 18, 1, celLight(p.detail, 0.22));
  px(ctx, cx - 9, headY + 1, 18, 1, celShadow(p.detail, 0.24));
  // A copa, mais estreita que a aba: sem ela o chapéu lê como prato.
  px(ctx, cx - 5, headY - 6, 10, 4, p.detail);
  px(ctx, cx - 5, headY - 6, 10, 1, celLight(p.detail, 0.3));
  px(ctx, cx - 5, headY - 4, 10, 1, celShadow(p.detail, 0.18));

  if (direction === Direction.North) return;

  // Saco de semente pendurado no cinto, e a corda que o segura.
  ellipse(ctx, cx + 6, shoulders + 11, 3, 3.6, p.shade);
  px(ctx, cx + 4, shoulders + 7, 5, 1, p.deep);
}

/** Barqueiro: gorro de lona e o remo em pé, mais alto que ele. */
function drawFerryman(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  drawVillager(ctx, direction, p, pose, ground, false);

  const cx = CREATURE_FRAME / 2;
  const shoulders = shoulderLine(p, pose, ground);
  const headY = ground - p.height + pose.breathe + 6;

  // Gorro: calota rente ao crânio, com uma dobra na testa. Vale nas quatro direções.
  ellipse(ctx, cx, headY - 3, 6.2, 4.2, p.accent);
  px(ctx, cx - 6, headY - 2, 12, 2, p.body);
  px(ctx, cx - 6, headY - 2, 12, 1, p.light);

  if (direction === Direction.North) return;

  // O remo, apoiado no chão e passando da cabeça: é a peça mais alta de todos os dez
  // desenhos, e é ela que se enxerga na ponta de um trapiche antes de qualquer cor.
  stave(ctx, cx + 7, headY - 8, ground - 1, p.detail, celLight(p.detail, 0.3));
  // A pá do remo, larga, no alto.
  px(ctx, cx + 5, headY - 12, 6, 6, p.detail);
  px(ctx, cx + 5, headY - 12, 6, 1, celLight(p.detail, 0.34));
  px(ctx, cx + 5, headY - 7, 6, 1, celShadow(p.detail, 0.3));
}

/** Curtidor: avental de couro e a pele crua dobrada sobre o ombro. */
function drawTanner(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  drawVillager(ctx, direction, p, pose, ground, false);

  const cx = CREATURE_FRAME / 2;
  const shoulders = shoulderLine(p, pose, ground);

  if (direction === Direction.North) return;

  apron(ctx, p, shoulders, p.detail);

  // A pele sobre o ombro esquerdo, caindo até a cintura, com a franja recortada embaixo. É
  // ela que diz "curtidor" e não "mais um avental": a silhueta do ombro fica mais alta e mais
  // larga de um lado só, que é como alguém carregando couro de fato se parece.
  px(ctx, cx - 10, shoulders - 2, 7, 11, p.highlight);
  px(ctx, cx - 10, shoulders - 2, 7, 2, celLight(p.highlight, 0.24));
  px(ctx, cx - 9, shoulders + 9, 5, 2, p.detail);
  for (const dx of [-9, -7, -5]) px(ctx, cx + dx, shoulders + 11, 1, 2, p.shade);
}

/** Joalheira: manto ameixa, galão de ouro e o colar erguido. */
function drawJeweler(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  drawVillager(ctx, direction, p, pose, ground, true);

  const cx = CREATURE_FRAME / 2;
  const shoulders = shoulderLine(p, pose, ground);

  // Galão de ouro na gola, e ele fica visível também de costas: o dourado é a identidade
  // dela, e a Banqueira — a outra de manto — usa azul.
  px(ctx, cx - 6, shoulders, 12, 2, p.detail);

  if (direction === Direction.North) return;

  // O colar: a corrente em V e a pedra no vértice. Três pixels de pedra clara sobre manto
  // escuro é o maior contraste da cidade, e é onde o olho vai primeiro.
  for (let i = 0; i < 4; i++) {
    px(ctx, cx - 4 + i, shoulders + 3 + i, 1, 1, p.detail);
    px(ctx, cx + 3 - i, shoulders + 3 + i, 1, 1, p.detail);
  }
  ellipse(ctx, cx, shoulders + 8, 2.2, 2.2, '#a8e8f0');
  px(ctx, cx - 1, shoulders + 7, 1, 1, '#ffffff');

  // A lupa na mão direita: um aro com o vidro dentro.
  px(ctx, cx + 5, shoulders + 9, 4, 4, p.detail);
  px(ctx, cx + 6, shoulders + 10, 2, 2, '#d8f0f8');
}

/** Sacerdote: capuz caído, manto de linho e o sol do Alvorecer no peito. */
function drawPriest(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  drawVillager(ctx, direction, p, pose, ground, true);

  const cx = CREATURE_FRAME / 2;
  const shoulders = shoulderLine(p, pose, ground);
  const headY = ground - p.height + pose.breathe + 6;

  // Capuz caído, e ele **emoldura** o rosto em vez de cobri-lo: dois panos descendo dos lados
  // da cabeça mais a gola atrás dos ombros. A primeira versão era um trapézio desenhado por
  // cima da cabeça inteira — e como este painter roda **depois** de `drawVillager`, o pano
  // apagava rosto, cabelo e olhos: na tela sobrava um vulto escuro com o sol dourado do peito
  // flutuando no meio dele. É o mesmo cuidado que o elmo do Guarda precisa ter, e a diferença
  // é que o elmo cobre a cabeça de propósito.
  for (const side of [-1, 1]) {
    px(ctx, cx + side * 7 - (side < 0 ? 0 : 1), headY - 3, 2, 11, p.shade);
  }
  px(ctx, cx - 7, headY + 7, 15, 3, p.shade);
  px(ctx, cx - 7, headY + 7, 15, 1, p.light);

  if (direction === Direction.North) return;

  // O sol: um disco com quatro raios curtos, na cor que só ele e o granjeiro usam. É o
  // primeiro símbolo religioso do jogo, e ele existe porque o Sacerdote é o primeiro NPC
  // que explica por que o templo devolve a vida.
  const sun = shoulders + 8;
  ellipse(ctx, cx, sun, 3, 3, p.detail);
  ellipse(ctx, cx, sun, 1.4, 1.4, '#fff4d0');
  for (const [dx, dy] of [[0, -5], [0, 5], [-5, 0], [5, 0]] as const) {
    px(ctx, cx + dx, sun + dy, 1, 1, p.detail);
  }

  // Cordão na cintura, com as duas pontas caindo.
  px(ctx, cx - 6, shoulders + 14, 12, 1, p.detail);
  px(ctx, cx + 3, shoulders + 15, 1, 4, p.detail);
}

/** Guarda: elmo, lança em pé e o escudo no braço esquerdo. */
function drawGuard(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  drawVillager(ctx, direction, p, pose, ground, false);

  const cx = CREATURE_FRAME / 2;
  const shoulders = shoulderLine(p, pose, ground);
  const headY = ground - p.height + pose.breathe + 6;

  // Elmo: calota de aço com nasal. Nas quatro direções, porque um guarda de costas continua
  // sendo o guarda — e porque o brilho do aço no alto é o que o distingue de um aldeão a
  // vinte tiles de distância.
  ellipse(ctx, cx, headY - 2, 6.4, 5.4, p.detail);
  px(ctx, cx - 6, headY - 1, 13, 3, p.detail);
  px(ctx, cx - 6, headY - 1, 13, 1, '#dfe5ec');
  px(ctx, cx - 6, headY + 2, 13, 1, p.shade);

  if (direction === Direction.North) return;

  // Nasal, e os olhos por baixo dele continuam sendo os de `drawVillager`.
  px(ctx, cx - 1, headY - 1, 2, 6, p.detail);

  // Lança: encostada no ombro direito, ponta acima do elmo. É a peça de silhueta.
  stave(ctx, cx + 7, headY - 14, ground - 2, p.accent, celLight(p.accent, 0.3));
  ctx.fillStyle = p.detail;
  ctx.beginPath();
  ctx.moveTo(cx + 8, headY - 20);
  ctx.lineTo(cx + 11, headY - 13);
  ctx.lineTo(cx + 5, headY - 13);
  ctx.closePath();
  ctx.fill();

  // Escudo no braço esquerdo: um retângulo arredondado com o umbo no meio.
  px(ctx, cx - 11, shoulders + 3, 6, 10, p.light);
  px(ctx, cx - 11, shoulders + 3, 6, 1, p.highlight);
  px(ctx, cx - 11, shoulders + 12, 6, 1, p.deep);
  px(ctx, cx - 9, shoulders + 7, 2, 2, p.detail);
}

/**
 * Aldeão: o esqueleto de gente e nada mais.
 *
 * É o único dos onze que **não** acrescenta um pixel, e é intencional. Ele é a referência
 * contra a qual os dez uniformes se leem: um mundo em que todo mundo tem ferramenta na mão é
 * um mundo de figurantes fantasiados, e a graça de reconhecer o armeiro pelo martelo depende
 * de existir gente sem martelo.
 *
 * O que varia entre um aldeão e outro é o nome sobre a cabeça (ver `NpcTemplate.roster`), e
 * não a roupa: doze paletas para doze moradores seriam doze índices de atlas para dizer a
 * mesma coisa que um nome próprio já diz.
 */
function drawTownsfolk(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  drawVillager(ctx, direction, p, pose, ground, false);
}

/**
 * Galinha: corpo redondo, crista, bico e duas patas de arame.
 *
 * O primeiro bicho do jogo que não é inimigo, e o desenho tem uma exigência que nenhum
 * monstro tem: ele precisa ler como **inofensivo** de relance, senão o jogador tenta matá-lo
 * e descobre que não pode. Tudo aqui empurra nessa direção — o corpo é uma bola sem ângulo,
 * as patas são finas, não há dente nem garra, e a única cor saturada é a da crista.
 *
 * A cauda aponta para o lado oposto ao da cabeça, e é ela que dá direção ao bicho de perfil:
 * sem cauda, uma esfera com um bico é a mesma coisa virada para os dois lados.
 */
function drawChicken(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  const cx = CREATURE_FRAME / 2;
  const top = ground - p.height + pose.breathe;
  const profile = direction === Direction.East;
  /** O corpo, e o orçamento de altura inteiro sai daqui: 12 px do topo da crista ao pé. */
  const body = { x: cx + (profile ? -1 : 0), y: top + 6 };

  // Patas: duas linhas de um pixel com três dedos, alternando com o passo. É o passo que faz
  // a galinha ciscar em vez de deslizar, e é a espessura que a faz parecer ave.
  for (const [index, dx] of [-2, 2].entries()) {
    const swing = Math.round(pose.step * (index === 0 ? 1 : -1));
    px(ctx, body.x + dx + swing, body.y + 3, 1, ground - body.y - 3, p.detail);
    px(ctx, body.x + dx + swing - 1, ground - 1, 3, 1, p.detail);
  }

  // Cauda antes do corpo: ela nasce atrás do flanco, então o corpo tem de cobrir o pé dela.
  if (profile) {
    ctx.fillStyle = p.light;
    ctx.beginPath();
    ctx.moveTo(body.x - 3, body.y - 1);
    ctx.lineTo(body.x - 10, body.y - 6);
    ctx.lineTo(body.x - 7, body.y + 2);
    ctx.closePath();
    ctx.fill();
  } else {
    px(ctx, body.x - 2, body.y - 6, 4, 3, p.light);
  }

  volume(ctx, body.x, body.y, 5.4, 4.2, p, -0.25);

  // Asa: uma pluma dobrada no flanco, um tom acima do corpo.
  ellipse(ctx, body.x + 1.5, body.y + 0.5, 3, 2.2, p.highlight);

  // Cabeça, crista e barbela. A crista tem três dentes, porque dois leem como orelha.
  const head = { x: cx + (profile ? 3 : 0), y: top + 2 };
  ellipse(ctx, head.x, head.y, 2.8, 2.6, p.light);
  ellipse(ctx, head.x, head.y - 0.8, 1.8, 1.4, p.highlight);
  // O pescoço, que liga a cabeça ao corpo: sem ele a cabeça flutua acima do dorso.
  px(ctx, head.x - 1, head.y + 1, 2, body.y - head.y - 1, p.light);

  for (const dx of [-2, 0, 2]) px(ctx, head.x + dx, head.y - 4, 2, 2, p.accent);

  if (direction === Direction.North) return;

  // Barbela, bico e olho. De frente, dois olhos; de perfil, um — a mesma regra de
  // `drawVillager`, e o mesmo motivo: uma cabeça de perfil com dois olhos lê como máscara.
  px(ctx, head.x + (profile ? 1 : -1), head.y + 2, 2, 2, p.accent);

  const beak = profile ? head.x + 2 : head.x - 1;
  px(ctx, beak, head.y, 3, 2, p.detail);
  px(ctx, beak, head.y + 1, 2, 1, celShadow(p.detail, 0.3));

  if (profile) {
    beadEye(ctx, head.x, head.y - 1, p.eye);
    return;
  }

  beadEye(ctx, head.x - 2, head.y - 1, p.eye);
  beadEye(ctx, head.x + 1, head.y - 1, p.eye);
}

// --- Bestiário por espécie -----------------------------------------------------
//
// Os painters daqui para baixo seguem as mesmas três regras do topo do arquivo — um
// contorno só, cinco tons na ordem da luz, ar em volta — e uma quarta, própria deles:
// **o traço que dá nome à espécie vem primeiro**. A presa do javali, o olho do ciclope,
// a pinça do escorpião: se esse traço não sobreviver a 32 px, o resto do desenho não
// tem por que existir.

/** Morcego: corpo pequeno pendurado entre duas asas de membrana. Voa — não pisa o chão. */
function drawBat(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  const cx = CREATURE_FRAME / 2;
  // O "passo" do morcego é a batida de asa: no voo não há pata para alternar, e reusar o
  // `step` é o que faz a caminhada dele ler como voo sem uma coluna nova na folha.
  const flap = Math.round(pose.step * 2);
  const t = ground - p.height + pose.breathe * 2 - Math.abs(flap);
  const f = pose.lunge;

  // Asas antes do corpo, para ele cobrir a raiz delas. O recorte entre as pontas de baixo
  // é o que faz a membrana ler como couro esticado em dedos, e não como capa.
  for (const side of [-1, 1] as const) {
    const rootX = cx + side * 2 + f;
    const tipX = cx + side * 12 + f;

    ctx.fillStyle = p.shade;
    ctx.beginPath();
    ctx.moveTo(rootX, t + 6);
    ctx.lineTo(tipX, t + 1 + flap);
    ctx.lineTo(tipX - side, t + 8 + flap);
    ctx.lineTo(rootX + side * 4, t + 9);
    ctx.closePath();
    ctx.fill();

    // O dedo da asa, mais escuro, do ombro à ponta.
    curve(ctx, [rootX, t + 6], [(rootX + tipX) / 2, t + 2 + flap], [tipX, t + 1 + flap], 1, p.deep);
    // Membrana avermelhada por dentro.
    px(ctx, rootX + side * 3, t + 5 + flap, 2, 3, p.detail);
  }

  volume(ctx, cx + f, t + 6, 3.6, 3.2, p, -0.1);

  // Orelhas grandes demais para a cabeça — o traço de morcego que sobrevive a 32 px.
  px(ctx, cx - 3 + f, t + 1, 2, 3, p.body);
  px(ctx, cx + 1 + f, t + 1, 2, 3, p.body);
  px(ctx, cx - 3 + f, t + 2, 1, 1, p.detail);
  px(ctx, cx + 2 + f, t + 2, 1, 1, p.detail);

  // Focinho arrebitado.
  px(ctx, cx - 1 + f, t + 7, 3, 2, p.detail);

  if (direction !== Direction.North) {
    beadEye(ctx, cx - 2 + f, t + 5, p.eye);
    beadEye(ctx, cx + 1 + f, t + 5, p.eye);
  }
}

/**
 * Aranha: oito patas arqueadas e o corpo em dois bulbos.
 *
 * `giant` estica o vão das patas e troca o salpico por uma marca clara no abdome: a
 * Aranha gigante precisa ser lida como a mesma espécie e como **outro problema**, e a
 * marca é o que se lê antes do tamanho.
 */
function spiderPainter(giant: boolean): Painter {
  const reach = giant ? 1.2 : 1;

  return (ctx, direction, p, pose, ground) => {
    const cx = CREATURE_FRAME / 2;
    const t = ground - p.height + pose.breathe;
    const f = pose.lunge;

    // Patas antes do corpo, para a junta sumir sob os bulbos. Elas sobem acima da linha
    // do dorso antes de descer — é esse arco que separa aranha de besouro de patas retas.
    for (const [i, tip] of [-11, -5, 3, 9].entries()) {
      const lifted = (i % 2 === 0) === (pose.step > 0) ? 1 : 0;
      const tipX = cx + Math.round(tip * reach) + (tip > 0 ? f : 0);

      curve(
        ctx,
        [cx - 1, t + 8],
        [cx + Math.round(tip * 0.6), t - 2],
        [tipX, ground - lifted],
        1.5,
        i % 2 === 0 ? p.shade : p.body,
      );
    }

    // Abdome atrás, cefalotórax na frente.
    volume(ctx, cx - 3, t + 8, 5.8 * reach, 4.6 * reach, p, -0.2);
    volume(ctx, cx + 4 + f, t + 9, 3.4 * reach, 2.8 * reach, p, -0.1);

    if (giant) {
      // A cruz de quitina clara no abdome.
      px(ctx, cx - 4, t + 4, 2, 6, p.detail);
      px(ctx, cx - 6, t + 6, 6, 2, p.detail);
    } else {
      speckle(ctx, cx - 7, t + 5, 9, 5, p.detail, 0.1, 14);
    }

    // Quelíceras caindo da frente da cabeça.
    px(ctx, cx + 6 + f, t + 11, 1, 3, p.shade);
    px(ctx, cx + 8 + f, t + 11, 1, 3, p.shade);

    if (direction !== Direction.North) {
      beadEye(ctx, cx + 4 + f, t + 7, p.eye);
      beadEye(ctx, cx + 7 + f, t + 8, p.eye);
    }
  };
}

/** Javali: barril sobre patas curtas, focinho rombo e as presas claras viradas para cima. */
function drawBoar(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  const cx = CREATURE_FRAME / 2;
  const t = ground - p.height + pose.breathe;
  const swing = Math.round(pose.step * 2);
  const f = pose.lunge;

  // Rabo em gancho na garupa.
  curve(ctx, [cx - 9, t + 6], [cx - 12, t + 4], [cx - 11, t + 9], 1, p.shade);

  leg(ctx, cx - 7, t + 10, ground, 3, p, true);
  leg(ctx, cx + 3 + f, t + 10, ground, 3, p, true);
  leg(ctx, cx - 4, t + 10 + swing, ground, 3, p);
  leg(ctx, cx + 6 + f, t + 10 - swing, ground, 3, p);

  // Barril: o javali é um tronco só, sem cintura — garupa e peito quase da mesma altura.
  volume(ctx, cx - 3, t + 8, 7.5, 5.8, p, -0.2);
  volume(ctx, cx + 4 + f, t + 8.5, 5, 5, p, -0.1);

  // Crista de cerdas eriçadas: a serra no lombo é o que o separa das outras feras de
  // quatro patas antes de qualquer cor.
  for (let i = 0; i < 6; i++) {
    px(ctx, cx - 8 + i * 3, t + (i % 2 === 0 ? 0 : 1), 2, 3, p.accent);
  }
  speckle(ctx, cx - 8, t + 4, 12, 5, p.accent, 0.08, 12);

  // Cabeça baixa, focinho chato de ponta romba.
  volume(ctx, cx + 8 + f, t + 10, 3.6, 3.2, p, -0.1);
  px(ctx, cx + 11 + f, t + 9, 2, 4, p.detail);
  px(ctx, cx + 12 + f, t + 10, 1, 2, p.deep);

  // Presas de osso subindo da mandíbula — o traço que dá nome ao bicho.
  px(ctx, cx + 9 + f, t + 12, 1, 2, '#efe7d2');
  px(ctx, cx + 11 + f, t + 11, 1, 2, '#efe7d2');

  // Orelha triangular.
  px(ctx, cx + 6 + f, t + 5, 3, 3, p.shade);

  if (direction !== Direction.North) beadEye(ctx, cx + 8 + f, t + 8, p.eye);
}

/** Urso: a massa toda no ombro — a corcova mais alta que a garupa é a silhueta da espécie. */
function drawBear(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  const cx = CREATURE_FRAME / 2;
  const t = ground - p.height + pose.breathe;
  const swing = Math.round(pose.step * 2);
  const f = pose.lunge;

  // Patas grossas: coxa de quatro pixels, que nenhuma outra fera de 32 px tem.
  leg(ctx, cx - 8, t + 14, ground, 4, p, true);
  leg(ctx, cx + 3 + f, t + 14, ground, 4, p, true);
  leg(ctx, cx - 4, t + 14 + swing, ground, 4, p);
  leg(ctx, cx + 7 + f, t + 14 - swing, ground, 4, p);

  volume(ctx, cx - 3, t + 12, 7.2, 6, p, -0.2);
  // A corcova, mais alta que a garupa: é ela que diz "urso" à distância.
  volume(ctx, cx + 3 + f, t + 8.5, 6, 6, p, -0.12);
  speckle(ctx, cx - 8, t + 5, 13, 6, p.highlight, 0.05, 13);

  // Cabeça redonda, baixa em relação à corcova.
  volume(ctx, cx + 8 + f, t + 9, 3.4, 3.2, p, -0.1);
  // Orelha redonda — a do lobo é triangular, e a diferença se vê no topo da silhueta.
  ellipse(ctx, cx + 6 + f, t + 4.5, 1.8, 1.8, p.body);
  ellipse(ctx, cx + 6 + f, t + 4.8, 0.9, 0.9, p.accent);
  // Focinho curto e claro.
  px(ctx, cx + 10 + f, t + 9, 2, 3, p.detail);
  px(ctx, cx + 11 + f, t + 9, 1, 1, p.deep);

  // Garras da pata dianteira.
  px(ctx, cx + 7 + f, ground - 1, 3, 1, p.detail);

  if (direction !== Direction.North) beadEye(ctx, cx + 8 + f, t + 8, p.eye);
}

/** Ogro: o peso todo embaixo — pança clara, pernas curtas e a cabeça pequena demais. */
function drawOgre(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  const cx = CREATURE_FRAME / 2;
  const t = ground - p.height + pose.breathe;
  const swing = Math.round(pose.step * 2);
  const f = pose.lunge;

  leg(ctx, cx - 8, t + 24 + swing, ground, 6, p, true);
  leg(ctx, cx + 2, t + 24 - swing, ground, 6, p);

  arm(ctx, cx - 10 + f, t + 13, p, true);

  // O barrigão: é o volume que o separa do troll, cujo peso mora nos ombros.
  volume(ctx, cx, t + 18, 8, 7, p, -0.1);
  ellipse(ctx, cx + 1, t + 21, 5, 4, p.light);
  px(ctx, cx - 7, t + 24, 15, 2, p.deep);

  volume(ctx, cx + 1, t + 11, 6, 4.6, p, -0.15);
  speckle(ctx, cx - 5, t + 9, 11, 7, p.accent, 0.08, 16);

  arm(ctx, cx + 6 + f, t + 13, p, false);

  // Cabeça pequena de mandíbula larga, com as presas subindo do queixo.
  volume(ctx, cx + 2 + f, t + 5, 4, 3.6, p, -0.15);
  px(ctx, cx - 1 + f, t + 7, 7, 2, p.shade);
  px(ctx, cx + f, t + 7, 1, 2, p.detail);
  px(ctx, cx + 4 + f, t + 7, 1, 2, p.detail);

  if (direction !== Direction.North) eye(ctx, cx + 2 + f, t + 4, p.eye);
}

/** Ciclope: um bípede de pedreira em volta de um olho só — o olho é o desenho inteiro. */
function drawCyclops(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  const cx = CREATURE_FRAME / 2;
  const t = ground - p.height + pose.breathe;
  const swing = Math.round(pose.step * 2);
  const f = pose.lunge;

  leg(ctx, cx - 7, t + 27 + swing, ground, 5, p, true);
  leg(ctx, cx + 2, t + 27 - swing, ground, 5, p);

  arm(ctx, cx - 11 + f, t + 15, p, true);

  volume(ctx, cx, t + 21, 7.5, 6.5, p, -0.15);
  px(ctx, cx - 6, t + 26, 13, 3, p.deep);
  volume(ctx, cx + 1, t + 13, 6.5, 5, p, -0.15);
  speckle(ctx, cx - 5, t + 11, 11, 8, p.accent, 0.07, 17);

  arm(ctx, cx + 7 + f, t + 15, p, false);

  // Cabeça e, nela, o olho único: grande, branco e com íris — nenhuma outra criatura tem
  // um círculo claro desse tamanho na cara, e é isso que o identifica a qualquer zoom.
  volume(ctx, cx + 1 + f, t + 6, 4.6, 4.2, p, -0.15);
  px(ctx, cx - 3 + f, t + 2, 9, 2, p.accent);
  if (direction !== Direction.North) {
    ellipse(ctx, cx + 1 + f, t + 5.5, 2.6, 2.2, '#f2efe4');
    ellipse(ctx, cx + 1.5 + f, t + 5.5, 1.3, 1.3, p.eye);
    px(ctx, cx + 1 + f, t + 5, 1, 1, OUTLINE);
  }
  // Presa inferior.
  px(ctx, cx - 1 + f, t + 9, 2, 2, p.detail);
}

/** Escorpião: pinças à frente e a cauda armada sobre o dorso — a ameaça é a silhueta. */
function drawScorpion(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  const cx = CREATURE_FRAME / 2;
  const t = ground - p.height + pose.breathe;
  const f = pose.lunge;
  const wave = Math.round(pose.step);

  // Seis patas curtas.
  for (const [i, off] of [-7, -3, 1].entries()) {
    leg(ctx, cx + off, t + 8 + (i % 2 === 0 ? wave : -wave), ground, 2, p, true);
    leg(ctx, cx + off + 2, t + 8 - (i % 2 === 0 ? wave : -wave), ground, 2, p);
  }

  // Cauda antes do corpo, para ele cobrir a raiz: contas subindo da garupa e dobrando
  // sobre o dorso, com o ferrão apontado para a frente.
  const beads = [
    [-8, 4, 2.4],
    [-11, 0, 2.2],
    [-10, -4, 2],
    [-7, -6, 1.8],
  ] as const;
  for (const [dx, dy, r] of beads) {
    ellipse(ctx, cx + dx, t + 8 + dy, r, r, p.body);
    ellipse(ctx, cx + dx - 1, t + 7 + dy, r * 0.5, r * 0.5, p.light);
  }
  px(ctx, cx - 5, t + 1, 2, 2, p.detail);
  px(ctx, cx - 3, t + 2, 2, 1, p.deep);

  // Corpo chato em placas.
  volume(ctx, cx - 1, t + 8, 6.5, 3.4, p, -0.15);
  volume(ctx, cx + 5 + f, t + 9, 3.6, 2.6, p, -0.1);
  px(ctx, cx - 5, t + 6, 2, 1, p.deep);
  px(ctx, cx - 2, t + 6, 2, 1, p.deep);
  px(ctx, cx + 1, t + 6, 2, 1, p.deep);

  // Pinça da frente: braço curto e garra aberta em duas presas desiguais.
  px(ctx, cx + 7 + f, t + 9, 3, 2, p.shade);
  ellipse(ctx, cx + 10 + f, t + 9, 2.2, 1.8, p.body);
  px(ctx, cx + 10 + f, t + 6, 2, 2, p.body);
  px(ctx, cx + 11 + f, t + 10, 1, 2, p.shade);
  // A segunda pinça, do outro lado, um tom abaixo.
  px(ctx, cx + 6 + f, t + 11, 4, 2, p.shade);
  px(ctx, cx + 9 + f, t + 12, 2, 2, p.deep);

  if (direction !== Direction.North) beadEye(ctx, cx + 5 + f, t + 7, p.eye);
}

/** Vespa gigante: paira com as asas abertas, e o abdome listrado é o aviso. */
function drawWasp(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  const cx = CREATURE_FRAME / 2;
  // Voa: nunca encosta no chão, e o "passo" vira o tremor do voo parado.
  const t = ground - p.height + pose.breathe * 2 + Math.round(pose.step);
  const f = pose.lunge;

  // Asas translúcidas antes do corpo. O alfa **multiplica** o que a morte já aplicou e é
  // restaurado ao valor anterior — zerá-lo em 1 apagaria o esmaecimento do tombo.
  const alpha = ctx.globalAlpha;
  ctx.globalAlpha = alpha * 0.5;
  ellipse(ctx, cx - 3, t + 1, 6.5, 2.4, p.highlight);
  ellipse(ctx, cx + 2, t, 5, 2, p.highlight);
  ctx.globalAlpha = alpha;

  // Abdome listrado: amarelo e preto alternados, o vocabulário de perigo que o jogador
  // traz de fora do jogo.
  volume(ctx, cx - 4, t + 8, 6.2, 4.2, p, -0.15);
  px(ctx, cx - 9, t + 6, 2, 5, p.detail);
  px(ctx, cx - 5, t + 5, 2, 7, p.detail);
  px(ctx, cx - 1, t + 6, 2, 5, p.detail);
  // Ferrão.
  px(ctx, cx - 12, t + 8, 2, 1, p.detail);

  // Tórax e cabeça.
  volume(ctx, cx + 3, t + 8, 3.2, 3, p, -0.1);
  volume(ctx, cx + 7 + f, t + 9, 2.4, 2.2, p, -0.1);

  // Antena.
  curve(ctx, [cx + 8 + f, t + 7], [cx + 10 + f, t + 4], [cx + 12 + f, t + 5], 1, p.accent);

  // Patas recolhidas sob o tórax: vespa em voo não pisa.
  curve(ctx, [cx + 2, t + 11], [cx + 3, t + 14], [cx + 6, t + 13], 1, p.accent);
  curve(ctx, [cx - 1, t + 11], [cx, t + 15], [cx + 3, t + 14], 1, p.accent);

  if (direction !== Direction.North) beadEye(ctx, cx + 7 + f, t + 8, p.eye);
}

/** Esqueleto: a gaiola de costelas e o vão entre as pernas — a ausência de carne é o desenho. */
function drawSkeleton(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  const cx = CREATURE_FRAME / 2;
  const t = ground - p.height + pose.breathe;
  const f = pose.lunge;
  const swing = Math.round(pose.step * 2);
  const hip = t + 18;

  // Pernas: canelas de osso, finas, com o joelho marcado.
  px(ctx, cx - 4, hip + swing, 2, ground - hip - swing, p.detail);
  px(ctx, cx + 2, hip - swing, 2, ground - hip + swing, p.detail);
  px(ctx, cx - 4, hip + 5, 2, 1, p.shade);
  px(ctx, cx + 2, hip + 5, 2, 1, p.shade);
  px(ctx, cx - 5, ground - 1, 4, 1, p.detail);
  px(ctx, cx + 2, ground - 1, 4, 1, p.detail);

  // Pélvis e coluna.
  px(ctx, cx - 4, hip - 3, 9, 3, p.body);
  px(ctx, cx - 1, t + 7, 2, 9, p.detail);

  // Caixa torácica: três costelas bastam para o olho ler "gaiola" — mais vira listrado.
  for (let i = 0; i < 3; i++) {
    px(ctx, cx - 5, t + 8 + i * 3, 11, 2, p.detail);
  }
  px(ctx, cx - 6, t + 6, 13, 2, p.light);

  // Braços de osso caídos, mãos em pinça.
  px(ctx, cx - 8, t + 8, 2, 9, p.body);
  px(ctx, cx + 7, t + 8, 2, 9, p.body);
  px(ctx, cx - 9, t + 16, 3, 2, p.detail);
  px(ctx, cx + 7 + f, t + 16, 3, 2, p.detail);

  // Crânio por último: calota clara, órbitas fundas com o brilho morto-vivo, mandíbula.
  ellipse(ctx, cx + f, t + 3.5, 4.4, 4, p.detail);
  if (direction !== Direction.North) {
    px(ctx, cx - 3 + f, t + 3, 2, 2, p.accent);
    px(ctx, cx + 1 + f, t + 3, 2, 2, p.accent);
    px(ctx, cx - 3 + f, t + 3, 1, 1, p.eye);
    px(ctx, cx + 1 + f, t + 3, 1, 1, p.eye);
    px(ctx, cx - 2 + f, t + 6, 4, 1, p.accent);
    px(ctx, cx - 2 + f, t + 7, 4, 1, p.body);
  }
}

/** Carniçal: o bípede que anda agachado — a diagonal do tronco é a fome desenhada. */
function drawGhoul(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  const cx = CREATURE_FRAME / 2;
  const t = ground - p.height + pose.breathe;
  const f = pose.lunge;
  const swing = Math.round(pose.step * 2);

  leg(ctx, cx - 5, t + 20 + swing, ground, 3, p, true);
  leg(ctx, cx + 1, t + 20 - swing, ground, 3, p);

  arm(ctx, cx - 7 + f, t + 12, p, true);

  // Tronco curvado para a frente: o quadril fica atrás da cabeça, e é essa diagonal que o
  // separa do porte ereto do esqueleto.
  volume(ctx, cx - 1, t + 17, 5.5, 5, p, -0.25);
  volume(ctx, cx + 2 + f, t + 11, 5, 4.4, p, -0.2);
  // Costelas aparecendo sob a pele.
  px(ctx, cx - 2, t + 13, 6, 1, p.shade);
  px(ctx, cx - 1, t + 15, 6, 1, p.shade);
  speckle(ctx, cx - 4, t + 9, 9, 8, p.deep, 0.08, 15);

  // Braço da frente terminando em garras compridas.
  arm(ctx, cx + 5 + f, t + 12, p, false);
  px(ctx, cx + 5 + f, t + 24, 1, 3, p.detail);
  px(ctx, cx + 7 + f, t + 24, 1, 3, p.detail);
  px(ctx, cx + 9 + f, t + 24, 1, 2, p.detail);

  // Cabeça enfiada à frente dos ombros, mandíbula caída com dois dentes.
  volume(ctx, cx + 5 + f, t + 5, 3.8, 3.4, p, -0.15);
  px(ctx, cx + 4 + f, t + 7, 4, 2, p.deep);
  px(ctx, cx + 4 + f, t + 7, 1, 1, p.detail);
  px(ctx, cx + 6 + f, t + 7, 1, 1, p.detail);

  if (direction !== Direction.North) eye(ctx, cx + 5 + f, t + 4, p.eye);
}

/** Espectro: uma mortalha pairando, rasgada na barra, com o rosto por vazio. */
function drawSpectre(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  const cx = CREATURE_FRAME / 2;
  // Paira: a barra nunca encosta no chão, e o passo é a deriva do voo parado.
  const t = ground - p.height + pose.breathe * 2 + Math.round(pose.step);
  const f = pose.lunge;
  const hem = ground - 5 + pose.breathe;

  // Meio transparente de nascença, multiplicando o alfa que a morte já tiver aplicado —
  // e restaurando ao fim, para não apagar o esmaecimento do tombo.
  const alpha = ctx.globalAlpha;
  ctx.globalAlpha = alpha * 0.78;

  // Mortalha: trapézio do ombro à barra, terminando em farrapos — os dentes do
  // zigue-zague são o que diz "pano rasgado" em vez de "cone".
  ctx.fillStyle = p.body;
  ctx.beginPath();
  ctx.moveTo(cx - 5, t + 6);
  ctx.lineTo(cx + 5, t + 6);
  ctx.lineTo(cx + 8 + f, hem);
  ctx.lineTo(cx + 5 + f, hem - 4);
  ctx.lineTo(cx + 2, hem);
  ctx.lineTo(cx - 1, hem - 5);
  ctx.lineTo(cx - 4, hem);
  ctx.lineTo(cx - 6, hem - 3);
  ctx.lineTo(cx - 8, hem);
  ctx.closePath();
  ctx.fill();

  // Pregas e a borda de luz.
  px(ctx, cx - 3, t + 12, 1, hem - t - 16, p.shade);
  px(ctx, cx + 2, t + 14, 1, hem - t - 17, p.shade);
  px(ctx, cx - 5, t + 8, 2, hem - t - 12, p.light);

  // Mangas erguidas: dois cotos que fazem o gesto de agarrar sem ter mãos.
  px(ctx, cx - 9, t + 10, 3, 5, p.shade);
  px(ctx, cx + 6 + f, t + 9, 3, 5, p.shade);

  // Capuz com o vazio dentro: o rosto é a ausência, e os olhos moram nela.
  ellipse(ctx, cx + f * 0.5, t + 4, 5, 4.4, p.body);
  ellipse(ctx, cx + 1 + f * 0.5, t + 4.5, 3, 2.8, p.accent);
  ctx.globalAlpha = alpha;

  if (direction !== Direction.North) {
    px(ctx, cx - 1 + f, t + 4, 1, 2, p.eye);
    px(ctx, cx + 2 + f, t + 4, 1, 2, p.eye);
  }
}

/** Lich: o esqueleto que subiu de vida — manto, coroa e um orbe aceso entre as mãos. */
function drawLich(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  const cx = CREATURE_FRAME / 2;
  const t = ground - p.height + pose.breathe;
  const hx = Math.round(pose.lunge * 0.5);
  const shoulders = t + 11;

  // Manto até o chão, como o da banqueira — a silhueta de quem não precisa correr.
  ctx.fillStyle = p.body;
  ctx.beginPath();
  ctx.moveTo(cx - 5, shoulders);
  ctx.lineTo(cx + 5, shoulders);
  ctx.lineTo(cx + 8, ground);
  ctx.lineTo(cx - 8, ground);
  ctx.closePath();
  ctx.fill();
  px(ctx, cx - 3, shoulders + 4, 1, ground - shoulders - 5, p.shade);
  px(ctx, cx + 2, shoulders + 6, 1, ground - shoulders - 7, p.shade);
  px(ctx, cx - 5, shoulders + 1, 2, ground - shoulders - 3, p.light);
  px(ctx, cx - 7, ground - 2, 15, 2, p.deep);
  px(ctx, cx - 6, shoulders, 13, 3, p.shade);

  // Mangas erguidas à frente, mãos de osso, e o orbe aceso entre elas: o lich conjura
  // até parado — é o que o separa do esqueleto raso de espada.
  px(ctx, cx - 8, shoulders + 3, 3, 6, p.shade);
  px(ctx, cx + 5 + hx, shoulders + 3, 3, 6, p.shade);
  px(ctx, cx - 7, shoulders + 9, 2, 2, p.detail);
  px(ctx, cx + 5 + hx, shoulders + 9, 2, 2, p.detail);
  ellipse(ctx, cx + hx * 0.5, shoulders + 10, 2.4, 2.4, p.eye);
  px(ctx, cx - 1 + hx, shoulders + 9, 1, 1, EYE_LIGHT);

  // Crânio sob a coroa: osso e ouro, as duas coisas que sobraram dele.
  ellipse(ctx, cx + hx, t + 5, 4.2, 3.8, p.detail);
  px(ctx, cx - 4 + hx, t + 1, 9, 2, p.accent);
  px(ctx, cx - 4 + hx, t - 1, 2, 2, p.accent);
  px(ctx, cx + hx, t - 1, 2, 2, p.accent);
  px(ctx, cx + 3 + hx, t - 1, 2, 2, p.accent);

  if (direction !== Direction.North) {
    px(ctx, cx - 3 + hx, t + 4, 2, 2, p.deep);
    px(ctx, cx + 1 + hx, t + 4, 2, 2, p.deep);
    px(ctx, cx - 3 + hx, t + 4, 1, 1, p.eye);
    px(ctx, cx + 1 + hx, t + 4, 1, 1, p.eye);
  }
}

/** Elemental de terra: três lajes fora de prumo — o desalinhamento é o que diz "pedras". */
function drawEarthElemental(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  const cx = CREATURE_FRAME / 2;
  const t = ground - p.height + pose.breathe;
  const f = pose.lunge;
  const sway = Math.round(pose.step);

  px(ctx, cx - 8, t + 18, 16, ground - t - 18, p.shade);
  px(ctx, cx - 8, t + 18, 16, 2, p.body);
  px(ctx, cx - 6 + sway, t + 9, 13, 9, p.body);
  px(ctx, cx - 6 + sway, t + 9, 13, 2, p.light);
  px(ctx, cx - 3 - sway, t + 2, 8, 7, p.light);
  px(ctx, cx - 3 - sway, t + 2, 8, 2, p.highlight);

  // Rachaduras: são elas que fazem os retângulos lerem como rocha, não como caixote.
  px(ctx, cx - 2, t + 20, 1, 4, p.deep);
  px(ctx, cx + 4, t + 12, 1, 3, p.deep);
  px(ctx, cx - 4, t + 11, 3, 1, p.deep);

  // Punhos de pedra pendurados dos lados.
  px(ctx, cx - 12, t + 10, 3, 6, p.body);
  px(ctx, cx - 13, t + 16, 4, 4, p.light);
  px(ctx, cx + 9 + f, t + 11, 3, 6, p.shade);
  px(ctx, cx + 8 + f, t + 17, 4, 4, p.body);

  if (direction !== Direction.North) {
    px(ctx, cx - 1 - sway, t + 4, 2, 2, p.eye);
    px(ctx, cx + 3 - sway, t + 4, 2, 2, p.eye);
  }
}

/** Uma língua de chama: gota que sobe da base larga a uma ponta que balança com `sway`. */
function flame(
  ctx: CanvasRenderingContext2D,
  cx: number,
  base: number,
  top: number,
  radius: number,
  sway: number,
  color: string,
): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(cx - radius, base - 2);
  ctx.quadraticCurveTo(cx - radius - 1, (base + top) / 2, cx + sway, top);
  ctx.quadraticCurveTo(cx + radius + 1, (base + top) / 2 + 3, cx + radius, base - 2);
  ctx.closePath();
  ctx.fill();
  ellipse(ctx, cx, base - 3, radius, 3, color);
}

/** Elemental de fogo: três camadas da mesma língua, do vermelho de brasa ao núcleo claro. */
function drawFireElemental(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  const cx = CREATURE_FRAME / 2;
  const t = ground - p.height;
  const sway = Math.round(pose.step * 2) + pose.breathe;
  const f = pose.lunge;

  // É a transição entre as camadas — não o contorno — que faz a chama parecer luz.
  flame(ctx, cx, ground, t, 9, sway, p.shade);
  flame(ctx, cx + 1, ground, t + 4, 6, sway, p.body);
  flame(ctx, cx + 1, ground - 1, t + 9, 4, Math.round(sway / 2), p.detail);

  // Farrapos soltos: fogo não tem borda contínua.
  px(ctx, cx - 9, t + 8 - sway, 2, 3, p.body);
  px(ctx, cx + 8 + f, t + 12 + sway, 2, 4, p.body);
  px(ctx, cx - 7, t + 16, 1, 2, p.detail);

  // Olhos escuros dentro do núcleo: a única criatura cujo olho é a ausência de luz.
  if (direction !== Direction.North) {
    px(ctx, cx - 2, t + 14, 2, 3, p.accent);
    px(ctx, cx + 2, t + 14, 2, 3, p.accent);
  }
}

/** Losango de gelo solto, satélite do prisma. */
function shard(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  color: string,
): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(x, y - r);
  ctx.lineTo(x + r * 0.7, y);
  ctx.lineTo(x, y + r);
  ctx.lineTo(x - r * 0.7, y);
  ctx.closePath();
  ctx.fill();
}

/** Elemental de gelo: um prisma de arestas retas — o único corpo do bestiário sem curva. */
function drawIceElemental(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  const cx = CREATURE_FRAME / 2;
  const t = ground - p.height + pose.breathe;
  const f = pose.lunge;

  ctx.fillStyle = p.body;
  ctx.beginPath();
  ctx.moveTo(cx, t + 1);
  ctx.lineTo(cx + 6, t + 10);
  ctx.lineTo(cx + 4, ground - 2);
  ctx.lineTo(cx - 4, ground - 2);
  ctx.lineTo(cx - 6, t + 10);
  ctx.closePath();
  ctx.fill();

  // Faceta iluminada do lado da luz, e a aresta de brilho no meio.
  ctx.fillStyle = p.light;
  ctx.beginPath();
  ctx.moveTo(cx, t + 1);
  ctx.lineTo(cx - 6, t + 10);
  ctx.lineTo(cx - 3, ground - 2);
  ctx.lineTo(cx - 1, ground - 2);
  ctx.lineTo(cx - 1, t + 8);
  ctx.closePath();
  ctx.fill();
  px(ctx, cx - 1, t + 3, 1, 10, p.highlight);
  px(ctx, cx - 1, t + 2, 1, 1, p.detail);

  // Estilhaços orbitando: satélites que dizem "isto flutua por vontade própria".
  shard(ctx, cx - 10, t + 9 - pose.breathe, 3, p.light);
  shard(ctx, cx + 9 + f, t + 14 + pose.breathe, 2.5, p.body);
  shard(ctx, cx - 8, t + 20 + pose.breathe, 2, p.shade);

  if (direction !== Direction.North) {
    px(ctx, cx - 3, t + 11, 2, 2, p.eye);
    px(ctx, cx + 1, t + 11, 2, 2, p.eye);
  }
}

/** Elemental de energia: um núcleo e os raios em zigue-zague que saltam dele. */
function drawEnergyElemental(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  const cx = CREATURE_FRAME / 2;
  const t = ground - p.height + pose.breathe;
  const f = pose.lunge;
  const jitter = Math.round(pose.step);

  // Arcos primeiro: vértices duros de propósito — raio não faz curva.
  ctx.strokeStyle = p.detail;
  ctx.lineWidth = 1.5;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(cx - 1, t + 10);
  ctx.lineTo(cx - 5 + jitter, t + 5);
  ctx.lineTo(cx - 3 + jitter, t + 1);
  ctx.moveTo(cx + 2, t + 14);
  ctx.lineTo(cx + 7 - jitter, t + 18);
  ctx.lineTo(cx + 5 - jitter, ground - 4);
  ctx.moveTo(cx - 2, t + 15);
  ctx.lineTo(cx - 7 - jitter, t + 19);
  ctx.lineTo(cx - 5, ground - 6);
  ctx.stroke();

  // Núcleo: três esferas concêntricas, do vazio ao brilho.
  ellipse(ctx, cx + f, t + 12, 5.5, 5.5, p.shade);
  ellipse(ctx, cx + f, t + 12, 3.8, 3.8, p.body);
  ellipse(ctx, cx - 1 + f, t + 11, 2, 2, p.detail);

  // Faíscas em posições fixas — sorteá-las faria a criatura ferver parada.
  px(ctx, cx - 9, t + 8, 1, 1, p.highlight);
  px(ctx, cx + 8 + f, t + 6, 1, 1, p.highlight);
  px(ctx, cx + 9, t + 16, 1, 1, p.highlight);
  px(ctx, cx - 8, t + 21, 1, 1, p.highlight);

  if (direction !== Direction.North) {
    px(ctx, cx - 2 + f, t + 11, 1, 2, p.eye);
    px(ctx, cx + 1 + f, t + 11, 1, 2, p.eye);
  }
}

/** Lagarto: rente ao chão, com a cauda varrendo atrás — é ela que o separa dos roedores. */
function drawLizard(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  const cx = CREATURE_FRAME / 2;
  const t = ground - p.height + pose.breathe;
  const swing = Math.round(pose.step * 2);
  const f = pose.lunge;

  curve(ctx, [cx - 6, t + 8], [cx - 13, t + 9], [cx - 12, t + 3], 2.5, p.body);
  curve(ctx, [cx - 6, t + 8], [cx - 12, t + 9], [cx - 11, t + 4], 1, p.shade);

  // Patas abertas para os lados, como réptil pisa.
  leg(ctx, cx - 5, t + 8, ground, 2, p, true);
  leg(ctx, cx + 3 + f, t + 8, ground, 2, p, true);
  leg(ctx, cx - 2, t + 8 + swing, ground, 2, p);
  leg(ctx, cx + 6 + f, t + 8 - swing, ground, 2, p);

  volume(ctx, cx - 2, t + 7, 7, 3.8, p, -0.15);
  volume(ctx, cx + 5 + f, t + 7.5, 4, 3, p, -0.1);
  // Ventre claro.
  px(ctx, cx - 4, t + 9, 10, 2, p.detail);
  // Crista serrilhada do dorso.
  for (let i = 0; i < 4; i++) {
    px(ctx, cx - 5 + i * 3, t + 2, 1, 2, p.accent);
  }

  // Cabeça em cunha.
  volume(ctx, cx + 9 + f, t + 8, 3, 2.4, p, -0.1);
  px(ctx, cx + 11 + f, t + 8, 2, 2, p.body);
  px(ctx, cx + 12 + f, t + 8, 1, 1, p.deep);

  if (direction !== Direction.North) beadEye(ctx, cx + 9 + f, t + 6, p.eye);
}

/** Serpente: a fita em S sem pata nenhuma, com o pescoço erguido em bote permanente. */
function drawSerpent(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  const cx = CREATURE_FRAME / 2;
  const t = ground - p.height + pose.breathe;
  const f = pose.lunge;
  // Serpente não alterna passos: desliza — o `step` desloca as dobras do S.
  const slide = Math.round(pose.step * 2);

  curve(ctx, [cx - 12, ground - 2], [cx - 8 + slide, ground - 9], [cx - 1, ground - 3], 4.5, p.shade);
  curve(ctx, [cx - 1, ground - 3], [cx + 4 - slide, ground - 1], [cx + 9, ground - 4], 4.5, p.body);
  // Barriga clara na dobra da frente.
  curve(ctx, [cx, ground - 2], [cx + 4 - slide, ground - 1], [cx + 8, ground - 4], 1.5, p.detail);
  speckle(ctx, cx - 10, ground - 8, 8, 4, p.light, 0.12, 16);

  // Pescoço erguido e cabeça em cunha: o bote armado é a pose permanente da espécie.
  curve(ctx, [cx + 9, ground - 4], [cx + 12, t + 7], [cx + 9 + f, t + 3], 3, p.body);
  ellipse(ctx, cx + 9 + f, t + 2.5, 2.8, 2.2, p.body);
  px(ctx, cx + 10 + f, t + 1, 2, 2, p.light);
  // Língua bífida: um pixel de sangue na frente da cunha.
  px(ctx, cx + 12 + f, t + 2, 1, 1, '#d8506a');

  if (direction !== Direction.North) beadEye(ctx, cx + 9 + f, t + 2, p.eye);
}

/** Draconato: o réptil que ficou de pé — postura de gente, cauda e focinho de bicho. */
function drawDrakonid(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  const cx = CREATURE_FRAME / 2;
  const t = ground - p.height + pose.breathe;
  const f = pose.lunge;
  const swing = Math.round(pose.step * 2);

  // Cauda caindo do quadril até perto do chão — é ela que nega "humano" à primeira vista.
  curve(ctx, [cx - 3, t + 22], [cx - 11, t + 26], [cx - 12, ground - 2], 3, p.shade);

  leg(ctx, cx - 5, t + 24 + swing, ground, 4, p, true);
  leg(ctx, cx + 2, t + 24 - swing, ground, 4, p);

  arm(ctx, cx - 8, t + 14, p, true);

  volume(ctx, cx, t + 19, 6, 6.5, p, -0.15);
  volume(ctx, cx + 1 + f, t + 12, 5, 4.6, p, -0.15);
  // Placas do peito: fiadas claras, como o ventre de um lagarto posto em pé.
  for (let i = 0; i < 3; i++) {
    px(ctx, cx - 3, t + 16 + i * 3, 7, 1, p.highlight);
  }

  arm(ctx, cx + 5 + f, t + 14, p, false);

  // Cabeça com focinho alongado e a crista vermelha até a nuca.
  volume(ctx, cx + 2 + f, t + 6, 4, 3.6, p, -0.15);
  px(ctx, cx + 5 + f, t + 6, 4, 3, p.body);
  px(ctx, cx + 8 + f, t + 7, 1, 1, p.deep);
  px(ctx, cx - 1 + f, t + 1, 2, 3, p.detail);
  px(ctx, cx + 1 + f, t + 2, 2, 2, p.detail);

  if (direction !== Direction.North) eye(ctx, cx + 4 + f, t + 5, p.eye);
}

/** Dragão: a asa dobrada sobre o dorso e os chifres — o resto é um quadrúpede grande. */
function drawDragon(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  p: CreaturePalette,
  pose: Pose,
  ground: number,
): void {
  const cx = CREATURE_FRAME / 2;
  const t = ground - p.height + pose.breathe;
  const f = pose.lunge;
  const swing = Math.round(pose.step * 2);

  // Cauda longa com ponta de seta.
  curve(ctx, [cx - 7, t + 16], [cx - 13, t + 18], [cx - 12, t + 10], 3, p.shade);
  px(ctx, cx - 13, t + 8, 3, 3, p.shade);

  leg(ctx, cx - 6, t + 17, ground, 4, p, true);
  leg(ctx, cx + 4 + f, t + 17, ground, 4, p, true);
  leg(ctx, cx - 2, t + 17 + swing, ground, 4, p);
  leg(ctx, cx + 7 + f, t + 17 - swing, ground, 4, p);

  volume(ctx, cx - 1, t + 14, 7.5, 5.5, p, -0.2);
  volume(ctx, cx + 5 + f, t + 13, 5, 5, p, -0.1);
  // Ventre blindado em ouro velho.
  px(ctx, cx - 6, t + 17, 12, 2, p.detail);

  // A asa dobrada sobre o dorso, com o polegar de garra no alto: é o traço que separa
  // "dragão" de "lagarto grande", e por isso passa por cima do corpo.
  ctx.fillStyle = p.accent;
  ctx.beginPath();
  ctx.moveTo(cx - 8, t + 12);
  ctx.lineTo(cx - 2, t + 1);
  ctx.lineTo(cx + 5, t + 11);
  ctx.closePath();
  ctx.fill();
  curve(ctx, [cx - 2, t + 2], [cx - 4, t + 7], [cx - 7, t + 11], 1, p.deep);
  curve(ctx, [cx - 2, t + 2], [cx - 1, t + 7], [cx + 3, t + 11], 1, p.deep);
  px(ctx, cx - 3, t, 2, 2, p.detail);

  // Pescoço grosso e cabeça de focinho quadrado, com os chifres para trás.
  px(ctx, cx + 6 + f, t + 6, 4, 6, p.body);
  volume(ctx, cx + 8 + f, t + 5, 3.2, 3, p, -0.1);
  px(ctx, cx + 10 + f, t + 4, 2, 3, p.body);
  px(ctx, cx + 11 + f, t + 3, 1, 1, p.highlight);
  px(ctx, cx + 6 + f, t + 1, 2, 2, p.detail);
  px(ctx, cx + 8 + f, t + 2, 2, 1, p.detail);

  if (direction !== Direction.North) eye(ctx, cx + 8 + f, t + 4, p.eye);
}

// --- Animações ---------------------------------------------------------------

function registerAnimations(scene: Phaser.Scene, outfitId: number): void {
  const key = creatureTextureKey(outfitId);

  for (const direction of [
    Direction.North,
    Direction.East,
    Direction.South,
    Direction.West,
  ]) {
    const row = direction * CREATURE_COLUMNS;

    create(scene, creatureAnimationKey(outfitId, 'idle', direction), key,
      IDLE.map((f) => row + f), 2, -1);
    create(scene, creatureAnimationKey(outfitId, 'walk', direction), key,
      WALK.map((f) => row + f), 10, -1);
    create(scene, creatureAnimationKey(outfitId, 'attack', direction), key,
      ATTACK.map((f) => row + f), 8, 0);
    // Morte não repete e o último quadro fica: é ele que o corpo no chão mostra até o
    // sprite ser destruído.
    create(scene, creatureAnimationKey(outfitId, 'death', direction), key,
      DEATH.map((f) => row + f), 8, 0);
    create(scene, creatureAnimationKey(outfitId, 'cast', direction), key,
      CAST.map((f) => row + f), 9, 0);
  }
}

function create(
  scene: Phaser.Scene,
  animationKey: string,
  textureKey: string,
  frames: number[],
  frameRate: number,
  repeat: number,
): void {
  if (scene.anims.exists(animationKey)) return;

  scene.anims.create({
    key: animationKey,
    frames: frames.map((frame) => ({ key: textureKey, frame })),
    frameRate,
    repeat,
  });
}
